import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';

/* ════════════════════════════════════════════════════════════════════════════
   VITAMIN CONCEPT TEST FORM
   Ported from: /home/ec2-user/concept-test-source/
   - styles.css (1236 lines)
   - screens.jsx (617 lines)
   - app.jsx (289 lines)
   - icons.jsx (81 lines)
   ════════════════════════════════════════════════════════════════════════════ */

const API_URL = import.meta.env.VITE_API_URL;

/* ═══════════════════════════════════════════════════════════════════════════
   CSS STYLES (from styles.css)
   ═══════════════════════════════════════════════════════════════════════════ */

interface PopulationOption {
  name: string;
  agent_count: number;
  displayName?: string;
}

const injectStyles = () => {
  if (document.getElementById('vitamin-concept-test-styles')) return;

  const style = document.createElement('style');
  style.id = 'vitamin-concept-test-styles';
  style.textContent = `
    /* ═══ CSS Variables ═══ */
    .vct-app {
      --teal: #07E5D1;
      --teal-bright: #0AF5E0;
      --teal-dark: #05A89B;
      --teal-soft: rgba(7, 229, 209, 0.06);
      --teal-glow: rgba(7, 229, 209, 0.12);
      --ink: #0a1f1c;
      --ink-soft: #2d4a45;
      --muted: #6b7f7c;
      --dim: #95a5a2;
      --bg: #f9fafb;
      --surface: #ffffff;
      --border: #e6ecea;
      --border-strong: #c7d6d3;
      --error: #dc2626;
      --error-soft: #fef2f2;
      --radius-sm: 8px;
      --radius: 12px;
      --radius-lg: 16px;
      --radius-xl: 22px;
      --shadow-sm: 0 1px 2px rgba(10, 31, 28, 0.04), 0 1px 3px rgba(10, 31, 28, 0.04);
      --shadow-md: 0 4px 14px rgba(10, 31, 28, 0.06), 0 2px 5px rgba(10, 31, 28, 0.04);
      --shadow-lg: 0 18px 38px rgba(10, 31, 28, 0.10), 0 6px 14px rgba(10, 31, 28, 0.05);
      --shadow-key: 0 1px 0 rgba(10, 31, 28, 0.20), inset 0 -1px 0 rgba(10, 31, 28, 0.05);
      --font-display: "Inter Tight", "Inter", -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
      --font-text: "Inter", -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
    }

    .vct-app {
      position: relative;
      min-height: 100vh;
      width: 100%;
      background: var(--bg);
      font-family: var(--font-text);
      color: var(--ink);
      -webkit-font-smoothing: antialiased;
    }

    /* ═══ Progress Bar ═══ */
    .vct-progress {
      position: fixed;
      top: 0; left: 0; right: 0;
      height: 4px;
      background: rgba(7, 229, 209, 0.08);
      z-index: 100;
    }
    .vct-progress__fill {
      height: 100%;
      width: 0;
      background: linear-gradient(90deg, var(--teal) 0%, var(--teal-bright) 100%);
      box-shadow: 0 0 12px var(--teal-glow);
      transition: width .55s cubic-bezier(.2,.8,.2,1);
    }

    /* ═══ Intro Banner ═══ */
    .vct-banner-wrap {
      padding: 64px 28px 0;
      display: flex;
      justify-content: center;
      position: relative;
      z-index: 30;
    }
    .vct-banner {
      width: 100%;
      max-width: 720px;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 14px;
      background: var(--teal-soft);
      border: 1px solid rgba(7, 229, 209, 0.28);
      border-left: 3px solid var(--teal);
      border-radius: 12px;
      box-shadow: var(--shadow-sm);
    }
    .vct-banner-enter {
      animation: vct-slideUp .45s cubic-bezier(.2,.8,.2,1) both;
    }
    .vct-banner__icon {
      flex-shrink: 0;
      width: 30px; height: 30px;
      border-radius: 50%;
      background: var(--surface);
      border: 1px solid rgba(7, 229, 209, 0.35);
      color: var(--teal);
      display: inline-flex; align-items: center; justify-content: center;
    }
    .vct-banner__icon svg { width: 15px; height: 15px; }
    .vct-banner__body {
      flex: 1;
      font-size: 13.5px;
      color: var(--ink-soft);
      line-height: 1.45;
    }
    .vct-banner__body strong {
      color: var(--ink);
      font-weight: 600;
    }
    .vct-banner__cta {
      flex-shrink: 0;
      background: var(--teal);
      color: white;
      border: none;
      padding: 7px 14px;
      border-radius: 7px;
      font-size: 13px; font-weight: 600;
      cursor: pointer;
      transition: background .18s;
    }
    .vct-banner__cta:hover { background: var(--teal-dark); }
    .vct-banner__close {
      flex-shrink: 0;
      width: 28px; height: 28px;
      border-radius: 6px;
      border: none;
      background: transparent;
      color: var(--muted);
      cursor: pointer;
      display: inline-flex; align-items: center; justify-content: center;
      transition: background .18s, color .18s;
    }
    .vct-banner__close:hover { background: rgba(0,0,0,.04); color: var(--ink); }
    .vct-banner__close svg { width: 14px; height: 14px; }
    .vct-app--banner .vct-screen { padding-top: 28px; }

    /* ═══ Top Bar ═══ */
    .vct-topbar {
      position: fixed; top: 0; left: 0; right: 0;
      display: flex; align-items: center; justify-content: space-between;
      padding: 18px 28px 14px;
      z-index: 40;
      pointer-events: none;
    }
    .vct-topbar > * { pointer-events: auto; }
    .vct-brand {
      display: inline-flex; align-items: center; gap: 10px;
      font-size: 13px; font-weight: 600; color: var(--ink-soft);
      white-space: nowrap;
    }
    .vct-brand__logo {
      display: inline-flex; align-items: center; gap: 7px;
      padding: 6px 11px;
      background: var(--ink);
      color: white;
      border-radius: 999px;
      font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
      text-transform: uppercase;
      white-space: nowrap;
      flex-shrink: 0;
    }
    .vct-brand__logo::before {
      content: "";
      width: 6px; height: 6px;
      border-radius: 50%;
      background: var(--teal-bright);
      box-shadow: 0 0 8px var(--teal-bright);
    }
    .vct-brand__sep { color: #d4dcdb; margin: 0 4px; }
    .vct-brand__title { color: var(--ink-soft); }
    .vct-exit-btn {
      border: 1px solid var(--border);
      background: rgba(255,255,255,.7);
      backdrop-filter: blur(8px);
      color: var(--ink-soft);
      padding: 8px 14px;
      border-radius: 8px;
      font-size: 13px; font-weight: 500;
      display: inline-flex; align-items: center; gap: 6px;
      transition: all .18s;
      cursor: pointer;
    }
    .vct-exit-btn:hover {
      border-color: var(--border-strong);
      color: var(--ink);
    }

    /* ═══ Screen Container ═══ */
    .vct-screen {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 100px 28px 140px;
      position: relative;
    }
    .vct-screen__inner {
      width: 100%;
      max-width: 720px;
    }
    .vct-screen-enter {
      animation: vct-slideUp .55s cubic-bezier(.2,.8,.2,1) both;
    }
    @keyframes vct-slideUp {
      from { opacity: 0; transform: translateY(28px); }
      to { opacity: 1; transform: translateY(0); }
    }

    /* ═══ Question Number ═══ */
    .vct-q-num {
      display: inline-flex; align-items: center; gap: 8px;
      color: var(--teal);
      font-size: 14px; font-weight: 600;
      margin-bottom: 18px;
      letter-spacing: 0.01em;
    }
    .vct-q-num svg { display: block; width: 11px; height: 11px; margin-left: 1px; }

    /* ═══ Headline ═══ */
    .vct-q-title {
      font-family: var(--font-display);
      font-size: clamp(28px, 4.2vw, 42px);
      font-weight: 600;
      line-height: 1.1;
      letter-spacing: -0.02em;
      color: var(--ink);
      margin: 0 0 14px;
    }
    .vct-q-title em {
      font-style: italic;
      color: var(--teal);
      font-weight: 500;
    }
    .vct-q-help {
      font-size: 17px;
      color: var(--muted);
      line-height: 1.55;
      margin: 0 0 36px;
      max-width: 60ch;
    }

    /* ═══ Big Input ═══ */
    .vct-big-input {
      width: 100%;
      border: none;
      border-bottom: 2px solid var(--border-strong);
      font-size: clamp(22px, 3vw, 30px);
      font-family: var(--font-display);
      font-weight: 400;
      padding: 12px 4px 14px;
      color: var(--ink);
      background: transparent;
      transition: border-color .2s;
      outline: none;
    }
    .vct-big-input::placeholder { color: #b6c2c0; font-weight: 300; }
    .vct-big-input:focus { border-bottom-color: var(--teal); }

    /* ═══ OK Button Row ═══ */
    .vct-ok-row {
      display: flex; align-items: center; gap: 16px;
      margin-top: 36px;
      flex-wrap: wrap;
    }
    .vct-ok-btn {
      display: inline-flex; align-items: center; gap: 10px;
      padding: 13px 22px;
      background: var(--teal);
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 16px; font-weight: 600;
      box-shadow: 0 4px 14px rgba(7, 229, 209, 0.30);
      transition: all .18s;
      cursor: pointer;
    }
    .vct-ok-btn:hover:not(:disabled) {
      background: var(--teal-dark);
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(7, 229, 209, 0.38);
    }
    .vct-ok-btn:disabled { opacity: .35; cursor: not-allowed; box-shadow: none; }
    .vct-ok-btn svg { width: 14px; height: 14px; }
    .vct-enter-hint {
      font-size: 13px;
      color: var(--muted);
      display: inline-flex; align-items: center; gap: 6px;
    }
    .vct-kbd {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 3px 7px;
      background: white;
      border: 1px solid var(--border-strong);
      border-radius: 5px;
      font-family: var(--font-display);
      font-size: 11px; font-weight: 600;
      color: var(--ink-soft);
      box-shadow: var(--shadow-key);
      line-height: 1;
    }

    /* ═══ Number Tiles ═══ */
    .vct-num-grid {
      display: flex; gap: 12px; flex-wrap: wrap;
      margin-bottom: 8px;
    }
    .vct-num-tile {
      width: 90px; height: 90px;
      border-radius: var(--radius-lg);
      border: 1.5px solid var(--border);
      background: var(--surface);
      font-family: var(--font-display);
      font-size: 32px; font-weight: 500;
      color: var(--ink);
      transition: all .18s cubic-bezier(.2,.8,.2,1);
      position: relative;
      overflow: hidden;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .vct-num-tile:hover {
      border-color: var(--border-strong);
      transform: translateY(-2px);
    }
    .vct-num-tile--selected {
      border-color: var(--teal);
      color: var(--teal);
      background: linear-gradient(135deg, var(--teal-soft) 0%, rgba(7, 229, 209, 0.02) 100%);
      box-shadow: 0 0 0 1px var(--teal), 0 12px 22px rgba(7, 229, 209, 0.16);
      transform: translateY(-2px);
    }
    .vct-num-tile__key {
      position: absolute;
      top: 6px; right: 8px;
      font-size: 10px; font-weight: 600;
      color: var(--dim);
      letter-spacing: 0.04em;
    }
    .vct-num-tile--selected .vct-num-tile__key { color: var(--teal); }

    .vct-mode-pill {
      display: inline-flex; align-items: center; gap: 10px;
      padding: 8px 14px;
      background: var(--teal-soft);
      border: 1px solid var(--border);
      border-radius: 999px;
      font-size: 13px;
      color: var(--ink-soft);
      margin-top: 18px;
    }
    .vct-mode-pill::before {
      content: "";
      width: 7px; height: 7px;
      border-radius: 50%;
      background: var(--teal);
      box-shadow: 0 0 0 4px var(--teal-glow);
    }

    /* ═══ Dropzone ═══ */
    .vct-dropzone {
      border: 2px dashed var(--border-strong);
      border-radius: var(--radius-lg);
      background: white;
      padding: 28px;
      text-align: center;
      transition: all .2s;
      cursor: pointer;
    }
    .vct-dropzone:hover, .vct-dropzone--drag {
      border-color: var(--teal);
      background: var(--teal-soft);
    }
    .vct-dropzone__icon {
      display: inline-flex; align-items: center; justify-content: center;
      width: 44px; height: 44px;
      border-radius: 11px;
      background: var(--teal-soft);
      color: var(--teal);
      margin-bottom: 12px;
    }
    .vct-dropzone__title {
      font-size: 16px; font-weight: 600;
      color: var(--ink); margin: 0 0 4px;
    }
    .vct-dropzone__title a {
      color: var(--teal); text-decoration: underline; text-underline-offset: 2px;
      cursor: pointer;
    }
    .vct-dropzone__hint {
      font-size: 13px; color: var(--muted);
    }

    .vct-split {
      display: flex; align-items: center;
      gap: 12px;
      margin: 18px 0;
      font-size: 12px; font-weight: 500;
      color: var(--dim); letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .vct-split::before, .vct-split::after {
      content: "";
      flex: 1; height: 1px;
      background: var(--border);
    }

    /* ═══ Requirements Hint ═══ */
    .vct-req-hint {
      margin: 12px 2px 18px;
      padding: 12px 14px;
      background: white;
      border: 1px solid var(--border);
      border-radius: 12px;
      font-size: 13px;
      line-height: 1.55;
      color: var(--ink-soft);
      display: flex;
      gap: 10px;
      align-items: flex-start;
    }
    .vct-req-hint__icon {
      flex-shrink: 0;
      width: 22px; height: 22px;
      border-radius: 6px;
      background: var(--teal-soft);
      color: var(--teal);
      display: inline-flex; align-items: center; justify-content: center;
      margin-top: 1px;
    }
    .vct-req-hint__body { flex: 1; min-width: 0; }
    .vct-req-hint__label {
      font-weight: 600;
      color: var(--ink);
      margin-bottom: 2px;
    }
    .vct-req-hint__list { color: var(--muted); }
    .vct-req-hint__list strong { color: var(--ink-soft); font-weight: 500; }
    .vct-req-hint__warn {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      margin-top: 6px;
      font-size: 12px;
      color: #b45309;
      font-weight: 500;
    }
    .vct-req-hint__warn::before {
      content: "";
      width: 5px; height: 5px;
      border-radius: 50%;
      background: #f59e0b;
      box-shadow: 0 0 0 3px rgba(245,158,11,.18);
    }

    /* ═══ See Sample - ANIMATED GRADIENT ═══ */
    .vct-see-sample {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      background: none;
      border: none;
      padding: 2px 0;
      margin-left: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      position: relative;
      background-image: linear-gradient(
        100deg,
        #07E5D1 0%,
        #0AF5E0 18%,
        #6366f1 38%,
        #ec4899 58%,
        #f59e0b 78%,
        #0AF5E0 92%,
        #07E5D1 100%
      );
      background-size: 280% 100%;
      background-position: 0% 50%;
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
      animation: vct-hueShift 5s linear infinite;
    }
    .vct-see-sample::after {
      content: "→";
      -webkit-text-fill-color: transparent;
      background: inherit;
      -webkit-background-clip: text;
      background-clip: text;
      display: inline-block;
      transition: transform .25s cubic-bezier(.2,.8,.2,1);
    }
    .vct-see-sample:hover::after { transform: translateX(3px); }
    .vct-see-sample::before {
      content: "";
      position: absolute;
      left: 0; right: 0; bottom: 0;
      height: 1.5px;
      background-image: inherit;
      background-size: 280% 100%;
      background-position: inherit;
      border-radius: 2px;
      opacity: .5;
      animation: vct-hueShift 5s linear infinite;
    }
    @keyframes vct-hueShift {
      0%   { background-position: 0% 50%; }
      100% { background-position: 280% 50%; }
    }

    /* ═══ Modal ═══ */
    .vct-modal-overlay {
      position: fixed; inset: 0;
      background: rgba(10, 31, 28, 0.45);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      z-index: 200;
      display: flex; align-items: center; justify-content: center;
      padding: 28px;
      animation: vct-fadeIn .2s ease-out;
    }
    @keyframes vct-fadeIn { from { opacity: 0; } to { opacity: 1; } }
    .vct-modal {
      background: white;
      border-radius: 18px;
      max-width: 720px;
      width: 100%;
      max-height: 90vh;
      overflow: auto;
      box-shadow: 0 30px 80px rgba(10, 31, 28, .35);
      animation: vct-popIn .3s cubic-bezier(.2,1.2,.4,1);
    }
    @keyframes vct-popIn {
      from { opacity: 0; transform: translateY(12px) scale(.96); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    .vct-modal__head {
      display: flex; align-items: center; justify-content: space-between;
      padding: 18px 22px 14px;
      border-bottom: 1px solid var(--border);
    }
    .vct-modal__title {
      font-family: var(--font-display);
      font-size: 18px; font-weight: 600;
      margin: 0;
      color: var(--ink);
    }
    .vct-modal__close {
      background: none; border: none;
      padding: 6px; border-radius: 6px;
      color: var(--muted);
      display: inline-flex;
      cursor: pointer;
    }
    .vct-modal__close:hover { background: var(--bg); color: var(--ink); }
    .vct-modal__body { padding: 22px; }

    /* ═══ Sample Card (Bottle) ═══ */
    .vct-sample-card {
      background: white;
      border: 1px solid var(--border);
      border-radius: 14px;
      padding: 28px 24px;
      display: grid;
      grid-template-columns: 180px 1fr;
      gap: 28px;
      align-items: start;
    }
    .vct-sample-card__left {
      display: flex; flex-direction: column;
      align-items: center;
      gap: 18px;
    }
    .vct-sample-card__bottle {
      width: 150px; height: 200px;
      position: relative;
      filter: drop-shadow(0 18px 24px rgba(10,31,28,.12));
    }
    .vct-sample-card__bottle__cap {
      position: absolute; left: 50%; top: 0;
      transform: translateX(-50%);
      width: 76px; height: 22px;
      background: linear-gradient(180deg, #4ade80 0%, #16a34a 100%);
      border-radius: 8px 8px 4px 4px;
    }
    .vct-sample-card__bottle__body {
      position: absolute; left: 50%; top: 18px;
      transform: translateX(-50%);
      width: 130px;
      bottom: 0;
      background: linear-gradient(180deg, #fafaf9 0%, #f5f5f4 100%);
      border-radius: 10px;
      border: 1px solid #e7e5e4;
      display: flex; flex-direction: column;
      align-items: center;
      padding: 14px 8px 10px;
      overflow: hidden;
    }
    .vct-sample-card__bottle__brand {
      font-family: var(--font-display);
      font-weight: 800;
      font-size: 14px;
      color: #16a34a;
      line-height: 1;
      letter-spacing: -0.01em;
      text-align: center;
      margin-bottom: 10px;
    }
    .vct-sample-card__bottle__brand span { display: block; }
    .vct-sample-card__bottle__panel {
      width: 100%;
      background: white;
      border-radius: 6px;
      padding: 8px 6px;
      text-align: center;
      border: 1px solid #f3f4f6;
      flex: 1;
      display: flex; flex-direction: column;
      align-items: center;
      gap: 4px;
    }
    .vct-sample-card__bottle__kids {
      font-family: var(--font-display);
      font-weight: 800;
      font-size: 11px;
      color: #16a34a;
      line-height: 1;
    }
    .vct-sample-card__bottle__type {
      font-size: 7px;
      font-weight: 600;
      color: #525252;
      letter-spacing: 0.02em;
    }
    .vct-sample-card__bottle__gummies {
      display: flex; gap: 2px;
      margin-top: 2px;
    }
    .vct-sample-card__bottle__gummies span {
      width: 10px; height: 10px;
      border-radius: 50%;
    }
    .vct-sample-card__bottle__owl {
      width: 28px; height: 28px;
      border-radius: 50%;
      background: #1e3a8a;
      position: relative;
      margin-top: 2px;
    }
    .vct-sample-card__bottle__owl::before,
    .vct-sample-card__bottle__owl::after {
      content: "";
      position: absolute;
      top: 8px;
      width: 7px; height: 7px;
      border-radius: 50%;
      background: white;
    }
    .vct-sample-card__bottle__owl::before { left: 5px; }
    .vct-sample-card__bottle__owl::after { right: 5px; }
    .vct-sample-card__bottle__count {
      position: absolute; left: 50%; bottom: 8px;
      transform: translateX(-50%);
      font-size: 9px;
      color: #737373;
      font-weight: 600;
    }
    .vct-sample-card__price-block {
      text-align: center;
      font-size: 14px;
      color: var(--ink-soft);
      line-height: 1.5;
    }
    .vct-sample-card__price {
      font-size: 18px;
      font-weight: 600;
      color: var(--ink);
      font-family: var(--font-display);
    }
    .vct-sample-card__per {
      font-size: 13px;
      color: var(--muted);
      margin-top: 2px;
    }
    .vct-sample-card__pack {
      margin-top: 14px;
      font-size: 13px;
      color: var(--ink-soft);
      line-height: 1.5;
    }
    .vct-sample-card__right { padding-top: 14px; }
    .vct-sample-card__name {
      font-family: var(--font-display);
      font-size: 18px;
      font-weight: 700;
      color: var(--ink);
      letter-spacing: -0.01em;
      margin: 0 0 18px;
      line-height: 1.25;
    }
    .vct-sample-card__desc {
      font-size: 14px;
      color: var(--ink-soft);
      line-height: 1.6;
      margin: 0 0 14px;
    }
    .vct-sample-card__desc:last-child { margin-bottom: 0; }

    .vct-sample-legend {
      margin-top: 16px;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 6px 14px;
      font-size: 12px;
      color: var(--muted);
    }
    .vct-sample-legend__row { display: flex; align-items: center; gap: 6px; }
    .vct-sample-legend__dot {
      width: 7px; height: 7px;
      border-radius: 50%;
      background: var(--teal);
      flex-shrink: 0;
    }

    /* ═══ Sample Template (Text Format) ═══ */
    .vct-sample-template {
      background: linear-gradient(135deg, #fafaf9 0%, #f8f8f6 100%);
      border: 1px solid var(--border);
      border-radius: 12px;
      padding: 20px;
    }
    .vct-sample-template__row {
      display: flex;
      gap: 8px;
      padding: 10px 0;
      border-bottom: 1px solid var(--border-soft);
    }
    .vct-sample-template__row:last-child { border-bottom: none; }
    .vct-sample-template__row--block {
      flex-direction: column;
      gap: 6px;
    }
    .vct-sample-template__label {
      font-weight: 700;
      color: var(--teal);
      font-size: 14px;
      flex-shrink: 0;
    }
    .vct-sample-template__value {
      font-size: 14px;
      color: var(--ink-soft);
      line-height: 1.5;
    }
    .vct-sample-template__list {
      margin: 0;
      padding-left: 20px;
      font-size: 14px;
      color: var(--ink-soft);
      line-height: 1.7;
    }
    .vct-sample-template__list li {
      padding: 2px 0;
    }

    /* ═══ Sample Visual Card ═══ */
    .vct-sample-visual {
      background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%);
      border-radius: 16px;
      padding: 24px;
    }
    .vct-sample-visual__card {
      background: white;
      border: 1px solid var(--border);
      border-radius: 12px;
      display: grid;
      grid-template-columns: 180px 1fr;
      overflow: hidden;
      box-shadow: 0 4px 20px rgba(0,0,0,.08);
    }
    .vct-sample-visual__left {
      background: linear-gradient(135deg, #f5f5f4 0%, #e8e8e6 100%);
      padding: 20px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 16px;
      border-right: 1px solid var(--border);
    }
    .vct-sample-visual__img {
      width: 120px;
      height: 120px;
      border-radius: 12px;
      background: white;
      border: 2px dashed var(--border);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      color: var(--muted);
      font-size: 11px;
      font-weight: 500;
    }
    .vct-sample-visual__price-box {
      text-align: center;
    }
    .vct-sample-visual__price {
      font-size: 20px;
      font-weight: 700;
      color: var(--ink);
      font-family: var(--font-display);
    }
    .vct-sample-visual__pack {
      font-size: 12px;
      color: var(--muted);
      margin-top: 2px;
    }
    .vct-sample-visual__right {
      padding: 20px 24px;
    }
    .vct-sample-visual__brand {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      color: var(--teal);
      margin-bottom: 6px;
    }
    .vct-sample-visual__headline {
      font-family: var(--font-display);
      font-size: 18px;
      font-weight: 700;
      color: var(--ink);
      margin: 0 0 8px;
      line-height: 1.3;
    }
    .vct-sample-visual__oneline {
      font-size: 13px;
      color: var(--ink-soft);
      margin: 0 0 12px;
      font-style: italic;
    }
    .vct-sample-visual__desc {
      font-size: 13px;
      color: var(--ink-soft);
      line-height: 1.6;
      margin: 0 0 14px;
    }
    .vct-sample-visual__rtb {
      background: var(--teal-soft);
      border-radius: 8px;
      padding: 12px 14px;
    }
    .vct-sample-visual__rtb-title {
      font-size: 11px;
      font-weight: 700;
      color: var(--teal);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 6px;
    }
    .vct-sample-visual__rtb ul {
      margin: 0;
      padding-left: 16px;
      font-size: 12px;
      color: var(--ink-soft);
      line-height: 1.6;
    }
    .vct-sample-visual__annotations {
      display: flex;
      flex-wrap: wrap;
      gap: 8px 16px;
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid var(--border);
    }
    .vct-sample-visual__anno {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: var(--muted);
    }
    .vct-sample-visual__dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--teal);
    }

    /* ═══ Concept Editor ═══ */
    .vct-concept-edit {
      border: 1.5px solid var(--border);
      border-radius: var(--radius-lg);
      background: white;
      overflow: hidden;
      transition: border-color .15s;
    }
    .vct-concept-edit:focus-within {
      border-color: var(--teal);
      box-shadow: 0 0 0 3px var(--teal-glow);
    }
    .vct-concept-edit__bar {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 12px;
      border-bottom: 1px solid var(--border);
      background: #fbfcfc;
    }
    .vct-tool-btn {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 6px 11px;
      background: white;
      border: 1px solid var(--border);
      border-radius: 7px;
      font-size: 12px; font-weight: 500;
      color: var(--ink-soft);
      transition: all .15s;
      cursor: pointer;
    }
    .vct-tool-btn:hover {
      border-color: var(--border-strong);
      background: var(--bg);
    }
    .vct-tool-btn svg { width: 13px; height: 13px; }
    .vct-concept-edit__textarea {
      width: 100%;
      min-height: 320px;
      padding: 16px 18px;
      border: none;
      outline: none;
      resize: vertical;
      font-family: var(--font-text);
      font-size: 14px;
      line-height: 1.65;
      color: var(--ink-soft);
      background: white;
    }
    .vct-concept-edit__textarea::placeholder { color: var(--dim); }
    .vct-concept-edit__textarea strong {
      font-weight: 600;
      color: var(--ink);
    }
    .vct-concept-edit__textarea:empty::before {
      content: attr(data-placeholder);
      color: var(--dim);
      pointer-events: none;
      white-space: pre-wrap;
    }

    /* ═══ Uploaded File ═══ */
    .vct-uploaded-file {
      display: flex; align-items: center; gap: 12px;
      padding: 12px 14px;
      background: white;
      border: 1px solid var(--border);
      border-radius: var(--radius);
      margin-bottom: 12px;
    }
    .vct-uploaded-file__thumb {
      width: 40px; height: 40px;
      border-radius: 8px;
      background: linear-gradient(135deg, #fef3c7, #fde68a);
      display: inline-flex; align-items: center; justify-content: center;
      font-size: 22px;
      flex-shrink: 0;
    }
    .vct-uploaded-file__name {
      flex: 1;
      font-size: 14px; font-weight: 500;
      color: var(--ink);
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .vct-uploaded-file__meta {
      font-size: 12px;
      color: var(--muted);
    }
    .vct-uploaded-file__remove {
      background: none; border: none;
      color: var(--dim);
      padding: 4px; border-radius: 5px;
      display: inline-flex;
      cursor: pointer;
    }
    .vct-uploaded-file__remove:hover { color: var(--error); background: var(--error-soft); }

    /* ═══ Concept Tabs ═══ */
    .vct-concept-tabs {
      display: flex; gap: 8px; flex-wrap: wrap;
      margin-bottom: 22px;
    }
    .vct-concept-tab {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 8px 14px 8px 8px;
      background: white;
      border: 1.5px solid var(--border);
      border-radius: 999px;
      font-size: 13px; font-weight: 500;
      color: var(--muted);
      transition: all .15s;
      cursor: pointer;
    }
    .vct-concept-tab:hover { border-color: var(--border-strong); color: var(--ink-soft); }
    .vct-concept-tab--active {
      border-color: var(--teal);
      color: var(--teal);
      background: var(--teal-soft);
    }
    .vct-concept-tab--done { color: var(--ink); }
    .vct-concept-tab__num {
      display: inline-flex; align-items: center; justify-content: center;
      width: 22px; height: 22px;
      border-radius: 50%;
      background: var(--bg);
      border: 1px solid var(--border);
      font-size: 11px; font-weight: 600;
      color: var(--muted);
    }
    .vct-concept-tab--active .vct-concept-tab__num {
      background: var(--teal);
      border-color: var(--teal);
      color: white;
    }
    .vct-concept-tab--done .vct-concept-tab__num {
      background: var(--ink);
      border-color: var(--ink);
      color: white;
    }

    /* ═══ Choice Cards ═══ */
    .vct-choices {
      display: flex; flex-direction: column;
      gap: 12px;
      margin: 8px 0 0;
    }
    .vct-choice {
      display: flex; align-items: center; gap: 14px;
      padding: 14px 18px;
      background: var(--surface);
      border: 1.5px solid var(--border);
      border-radius: var(--radius);
      text-align: left;
      width: 100%;
      font-size: 17px;
      color: var(--ink);
      transition: all .15s;
      position: relative;
      cursor: pointer;
    }
    .vct-choice:hover {
      border-color: var(--teal);
      background: var(--teal-soft);
      transform: translateX(2px);
    }
    .vct-choice--selected {
      border-color: var(--teal);
      background: var(--teal-soft);
      box-shadow: 0 0 0 3px var(--teal-glow);
    }
    .vct-choice__key {
      display: inline-flex; align-items: center; justify-content: center;
      width: 26px; height: 26px;
      border-radius: 5px;
      border: 1.5px solid var(--border-strong);
      background: white;
      font-family: var(--font-display);
      font-size: 12px; font-weight: 600;
      color: var(--ink-soft);
      flex-shrink: 0;
    }
    .vct-choice--selected .vct-choice__key {
      background: var(--teal);
      border-color: var(--teal);
      color: white;
    }
    .vct-choice__label {
      flex: 1;
      font-weight: 500;
      line-height: 1.3;
    }
    .vct-choice__desc {
      font-size: 14px;
      color: var(--muted);
      font-weight: 400;
      margin-top: 3px;
      line-height: 1.4;
    }
    .vct-choice__meta {
      font-size: 13px;
      color: var(--muted);
      flex-shrink: 0;
    }
    .vct-choice__check {
      width: 20px; height: 20px;
      flex-shrink: 0;
      color: var(--teal);
      opacity: 0;
      transition: opacity .15s;
    }
    .vct-choice--selected .vct-choice__check { opacity: 1; }

    /* ═══ Review Card ═══ */
    .vct-review-card {
      background: white;
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      overflow: hidden;
      margin-bottom: 20px;
    }
    .vct-review-row {
      display: flex; align-items: flex-start; gap: 18px;
      padding: 16px 22px;
      border-bottom: 1px solid var(--border);
    }
    .vct-review-row:last-child { border-bottom: none; }
    .vct-review-row__label {
      font-size: 13px;
      color: var(--muted);
      width: 140px;
      flex-shrink: 0;
      padding-top: 2px;
      font-weight: 500;
    }
    .vct-review-row__value {
      flex: 1;
      font-size: 15px;
      color: var(--ink);
      font-weight: 500;
      line-height: 1.45;
      min-width: 0;
    }
    .vct-review-row__edit {
      background: none; border: none;
      font-size: 13px;
      color: var(--teal);
      display: inline-flex; align-items: center; gap: 4px;
      padding: 4px 8px; border-radius: 6px;
      transition: background .15s;
      flex-shrink: 0;
      cursor: pointer;
    }
    .vct-review-row__edit:hover { background: var(--teal-soft); }
    .vct-concept-chip {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 4px 10px 4px 4px;
      background: var(--bg);
      border: 1px solid var(--border);
      border-radius: 999px;
      font-size: 13px;
      color: var(--ink-soft);
      margin: 3px 4px 3px 0;
    }
    .vct-concept-chip__num {
      display: inline-flex; align-items: center; justify-content: center;
      width: 20px; height: 20px;
      border-radius: 50%;
      background: var(--teal);
      color: white;
      font-size: 11px; font-weight: 600;
    }

    /* ═══ Done / Loading Screen ═══ */
    .vct-done-screen {
      text-align: center;
      display: flex; flex-direction: column; align-items: center;
      gap: 18px;
    }
    .vct-done-icon {
      width: 88px; height: 88px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--teal) 0%, var(--teal-bright) 100%);
      color: white;
      display: inline-flex; align-items: center; justify-content: center;
      box-shadow: 0 18px 40px rgba(7, 229, 209, 0.30);
      margin-bottom: 8px;
      animation: vct-pop .55s cubic-bezier(.2, 1.4, .4, 1) .1s both;
    }
    @keyframes vct-pop {
      from { transform: scale(0); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }
    .vct-done-icon svg { width: 38px; height: 38px; }
    .vct-done-title {
      font-family: var(--font-display);
      font-size: clamp(30px, 4vw, 44px);
      font-weight: 600;
      letter-spacing: -0.02em;
      color: var(--ink);
      margin: 0;
    }
    .vct-done-subtitle {
      font-size: 17px;
      color: var(--muted);
      margin: 0;
      max-width: 50ch;
      line-height: 1.55;
    }
    .vct-done-meta {
      display: inline-flex; align-items: center; gap: 24px;
      padding: 14px 22px;
      background: white;
      border: 1px solid var(--border);
      border-radius: 12px;
      margin-top: 14px;
    }
    .vct-done-meta__item {
      display: flex; flex-direction: column; gap: 2px;
    }
    .vct-done-meta__num {
      font-family: var(--font-display);
      font-size: 24px; font-weight: 600;
      color: var(--ink);
      line-height: 1;
    }
    .vct-done-meta__label {
      font-size: 11px;
      color: var(--muted);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      font-weight: 500;
    }
    .vct-done-meta__sep {
      width: 1px;
      align-self: stretch;
      background: var(--border);
    }
    .vct-spinner {
      width: 38px; height: 38px;
      border: 3px solid var(--border);
      border-top-color: var(--teal);
      border-radius: 50%;
      animation: vct-spin 0.8s linear infinite;
    }
    @keyframes vct-spin { to { transform: rotate(360deg); } }
    .vct-loading-bar {
      width: 100%; max-width: 320px;
      height: 6px;
      background: var(--bg);
      border-radius: 3px;
      overflow: hidden;
      margin-top: 18px;
    }
    .vct-loading-bar__fill {
      height: 100%;
      background: linear-gradient(90deg, var(--teal), var(--teal-bright));
      border-radius: 3px;
      width: 0;
      transition: width .3s;
    }

    /* ═══ Step Nav ═══ */
    .vct-step-nav {
      position: fixed;
      bottom: 24px; right: 24px;
      display: flex; align-items: center; gap: 14px;
      z-index: 50;
    }
    .vct-step-counter {
      font-size: 13px;
      color: var(--muted);
    }
    .vct-step-counter strong {
      color: var(--ink);
      font-family: var(--font-display);
      font-weight: 600;
    }
    .vct-step-nav__btns {
      display: flex;
      background: var(--ink);
      border-radius: 8px;
      overflow: hidden;
      box-shadow: var(--shadow-md);
    }
    .vct-step-nav__btn {
      width: 38px; height: 38px;
      background: transparent;
      border: none;
      color: white;
      display: inline-flex; align-items: center; justify-content: center;
      transition: background .15s;
      cursor: pointer;
    }
    .vct-step-nav__btn:hover:not(:disabled) {
      background: rgba(255,255,255, 0.10);
    }
    .vct-step-nav__btn:disabled {
      opacity: .3;
      cursor: not-allowed;
    }
    .vct-step-nav__btn + .vct-step-nav__btn {
      border-left: 1px solid rgba(255,255,255,.15);
    }
    .vct-step-nav__btn svg { width: 14px; height: 14px; }

    /* ═══ Admin Panel ═══ */
    .vct-admin-panel {
      margin-top: 24px;
      padding: 16px;
      background: white;
      border: 1px solid var(--border);
      border-radius: var(--radius);
    }
    .vct-admin-panel__toggle {
      display: flex; align-items: center; gap: 8px;
      width: 100%;
      padding: 8px 0;
      background: none;
      border: none;
      color: var(--muted);
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: color .15s;
    }
    .vct-admin-panel__toggle:hover { color: var(--ink); }
    .vct-admin-panel__content {
      padding-top: 12px;
      display: flex; flex-direction: column; gap: 12px;
    }
    .vct-admin-field {
      display: flex; flex-direction: column; gap: 4px;
    }
    .vct-admin-field label {
      font-size: 12px;
      font-weight: 500;
      color: var(--ink-soft);
    }
    .vct-admin-field input,
    .vct-admin-field select {
      padding: 8px 12px;
      border: 1px solid var(--border);
      border-radius: 6px;
      font-size: 14px;
      color: var(--ink);
      background: white;
      outline: none;
    }
    .vct-admin-field input:focus,
    .vct-admin-field select:focus {
      border-color: var(--teal);
    }

    /* ═══ Concept Grid ═══ */
    .vct-concept-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
      gap: 14px;
      margin: 14px 0 0;
    }
    .vct-concept-card {
      position: relative;
      background: white;
      border: 1.5px solid var(--border);
      border-radius: 14px;
      overflow: hidden;
      aspect-ratio: 3 / 4;
      transition: all .18s;
      display: flex; flex-direction: column;
      cursor: default;
    }
    .vct-concept-card:hover {
      border-color: var(--border-strong);
      transform: translateY(-2px);
      box-shadow: var(--shadow-md);
    }
    .vct-concept-card__thumb {
      position: relative;
      flex: 1;
      background: linear-gradient(135deg, #f5f5f4 0%, #eaeae8 100%);
      display: flex; align-items: center; justify-content: center;
      font-size: 38px;
      overflow: hidden;
      position: relative;
    }
    .vct-concept-card__thumb img {
      width: 100%; height: 100%; object-fit: cover;
      display: block;
    }
    .vct-concept-card__thumb--text {
      background: linear-gradient(135deg, var(--teal-soft), rgba(7,229,209,.02));
      color: var(--teal);
    }
    .vct-concept-card__meta {
      padding: 9px 12px;
      border-top: 1px solid var(--border);
      background: white;
    }
    .vct-concept-card__num {
      font-size: 10px; font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--teal);
      margin-bottom: 2px;
    }
    .vct-concept-card__name {
      font-size: 12px;
      font-weight: 600;
      color: var(--ink);
      line-height: 1.3;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .vct-concept-card__source {
      position: absolute;
      top: 8px; left: 8px;
      background: rgba(255,255,255,.92);
      backdrop-filter: blur(4px);
      border-radius: 6px;
      padding: 3px 7px;
      font-size: 10px; font-weight: 600;
      color: var(--ink-soft);
      display: inline-flex; align-items: center; gap: 4px;
    }
    .vct-concept-card__source svg { width: 11px; height: 11px; }
    .vct-concept-card__remove {
      position: absolute;
      top: 8px; right: 8px;
      z-index: 5;
      width: 26px; height: 26px;
      border-radius: 50%;
      background: rgba(239,68,68,.9);
      backdrop-filter: blur(4px);
      color: white;
      border: none;
      display: inline-flex; align-items: center; justify-content: center;
      opacity: 0;
      transition: opacity .15s, background .15s, transform .15s;
      cursor: pointer;
    }
    .vct-concept-card:hover .vct-concept-card__remove { opacity: 1; }
    .vct-concept-card__remove:hover { background: var(--error); transform: scale(1.1); }
    .vct-concept-card__remove svg { width: 13px; height: 13px; }

    /* Hover hint overlay */
    .vct-concept-card__hover-hint {
      position: absolute;
      inset: 0;
      background: rgba(7,229,209,.65);
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      gap: 6px;
      opacity: 0;
      transition: opacity .2s;
      pointer-events: none;
      z-index: 2;
    }
    .vct-concept-card:hover .vct-concept-card__hover-hint { opacity: 1; }
    .vct-concept-card__hover-hint svg { width: 22px; height: 22px; color: white; }
    .vct-concept-card__hover-hint span {
      font-size: 11px; font-weight: 600;
      color: white;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .vct-concept-card--parsing .vct-concept-card__thumb::after {
      content: "";
      position: absolute; inset: 0;
      background: rgba(255,255,255,.7);
      backdrop-filter: blur(2px);
    }
    .vct-concept-card--parsing::before {
      content: "";
      position: absolute;
      top: calc(50% - 13px);
      left: calc(50% - 13px);
      z-index: 10;
      width: 26px; height: 26px;
      border: 3px solid rgba(7,229,209,.2);
      border-top-color: var(--teal);
      border-radius: 50%;
      animation: vct-spin .8s linear infinite;
    }
    .vct-concept-card__done {
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      width: 48px; height: 48px;
      border-radius: 50%;
      background: #10B981;
      color: white;
      display: inline-flex; align-items: center; justify-content: center;
      box-shadow: 0 4px 14px rgba(16, 185, 129, 0.35), 0 0 0 4px rgba(16, 185, 129, 0.15);
      pointer-events: none;
      z-index: 4;
      animation: vct-done-pop .5s cubic-bezier(.2, 1.6, .4, 1) both;
    }
    .vct-concept-card__done svg { width: 24px; height: 24px; stroke-width: 3; }
    @keyframes vct-done-pop {
      0%   { transform: translate(-50%, -50%) scale(0); opacity: 0; }
      60%  { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
      100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
    }
    .vct-concept-card:hover .vct-concept-card__done { opacity: 0; transition: opacity .2s; }

    /* ═══ Add Tile ═══ */
    .vct-add-tile {
      background: white;
      border: 1.5px dashed var(--border-strong);
      border-radius: 14px;
      aspect-ratio: 3 / 4;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      gap: 6px;
      color: var(--muted);
      transition: all .18s;
      cursor: pointer;
      font-size: 12px;
      font-weight: 500;
    }
    .vct-add-tile:hover {
      border-color: var(--teal);
      color: var(--teal);
      background: var(--teal-soft);
    }
    .vct-add-tile__plus {
      width: 36px; height: 36px;
      border-radius: 50%;
      background: var(--teal-soft);
      color: var(--teal);
      display: inline-flex; align-items: center; justify-content: center;
    }
    .vct-add-tile__plus svg { width: 18px; height: 18px; }

    /* ═══ Secondary Button ═══ */
    .vct-secondary-btn {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 16px 22px;
      background: white;
      border: 1.5px solid var(--border);
      border-radius: 14px;
      cursor: pointer;
      text-align: left;
      transition: all .18s;
      font-family: var(--font-text);
    }
    .vct-secondary-btn:hover {
      border-color: var(--teal);
      background: var(--teal-soft);
      transform: translateY(-1px);
    }
    .vct-secondary-btn__icon {
      width: 40px; height: 40px;
      flex-shrink: 0;
      border-radius: 10px;
      background: var(--teal-soft);
      color: var(--teal);
      display: inline-flex; align-items: center; justify-content: center;
    }
    .vct-secondary-btn__icon svg { width: 18px; height: 18px; }
    .vct-secondary-btn__title {
      font-size: 15px;
      font-weight: 600;
      color: var(--ink);
      line-height: 1.3;
      margin-bottom: 2px;
    }
    .vct-secondary-btn__sub {
      font-size: 13px;
      color: var(--muted);
    }
    .vct-secondary-btn__body { flex: 1; min-width: 0; }
    .vct-secondary-btn__arrow {
      color: var(--muted);
      flex-shrink: 0;
      transition: transform .2s, color .2s;
    }
    .vct-secondary-btn:hover .vct-secondary-btn__arrow {
      transform: translateX(3px);
      color: var(--teal);
    }
    .vct-secondary-btn__arrow svg { width: 16px; height: 16px; }

    /* ═══ OR Divider ═══ */
    .vct-or-divider {
      display: flex; align-items: center; gap: 14px;
      margin: 20px 0;
      font-size: 12px; font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--muted);
      white-space: nowrap;
    }
    .vct-or-divider::before, .vct-or-divider::after {
      content: "";
      flex: 1;
      height: 1px;
      background: var(--border);
    }

    /* ═══ Requirements Line ═══ */
    .vct-req-line {
      margin: 10px 2px 0;
      font-size: 13px;
      color: var(--muted);
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      line-height: 1.5;
    }
    .vct-req-line strong {
      color: var(--ink-soft);
      font-weight: 500;
    }
    .vct-req-line__sep {
      display: inline-block;
      width: 3px; height: 3px;
      border-radius: 50%;
      background: var(--border-strong);
      margin: 0 2px;
    }

    /* ═══ Manual Editor Modal ═══ */
    .vct-modal--editor {
      max-width: 800px;
    }
    .vct-modal--editor .vct-modal__body {
      padding: 0;
    }
    .vct-manual-modal__eyebrow {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--muted);
    }
    .vct-manual-modal__textarea {
      width: 100%;
      min-height: 400px;
      padding: 22px;
      border: none;
      font-family: var(--font-text);
      font-size: 15px;
      line-height: 1.65;
      color: var(--ink);
      resize: vertical;
      background: var(--bg);
      outline: none;
    }
    .vct-manual-modal__textarea::placeholder { color: var(--dim); }
    .vct-modal__foot {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 22px;
      border-top: 1px solid var(--border);
      background: white;
    }
    .vct-tool-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 12px;
      background: white;
      border: 1px solid var(--border);
      border-radius: 8px;
      font-size: 13px;
      font-weight: 500;
      color: var(--ink-soft);
      cursor: pointer;
      transition: all .15s;
    }
    .vct-tool-btn:hover {
      border-color: var(--border-strong);
      color: var(--ink);
    }
    .vct-tool-btn svg { width: 14px; height: 14px; }

    @media (max-width: 640px) {
      .vct-topbar { padding: 14px 18px 10px; }
      .vct-screen { padding: 90px 22px 130px; }
      .vct-num-tile { width: 72px; height: 72px; font-size: 26px; }
      .vct-review-row { flex-direction: column; gap: 6px; padding: 14px 18px; }
      .vct-review-row__label { width: auto; }
      .vct-sample-card {
        grid-template-columns: 1fr;
        gap: 18px;
        text-align: center;
      }
      .vct-sample-card__right { padding-top: 0; }
    }
  `;
  document.head.appendChild(style);
};

/* ═══════════════════════════════════════════════════════════════════════════
   ICONS (from icons.jsx)
   ═══════════════════════════════════════════════════════════════════════════ */

const Icon = {
  arrowRight: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  arrowLeft: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 12H5M11 5l-7 7 7 7" />
    </svg>
  ),
  check: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  chevronUp: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M18 15l-6-6-6 6" />
    </svg>
  ),
  chevronDown: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  upload: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
    </svg>
  ),
  fileText: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
    </svg>
  ),
  x: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  ),
  edit: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 20h9M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  ),
  sparkles: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3zM19 14l.8 2.4L22 17l-2.2.6L19 20l-.8-2.4L16 17l2.2-.6L19 14zM5 14l.8 2.4L8 17l-2.2.6L5 20l-.8-2.4L2 17l2.2-.6L5 14z" />
    </svg>
  ),
  clock: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
  alert: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  ),
  settings: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
  plus: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  image: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  ),
};

/* ═══════════════════════════════════════════════════════════════════════════
   SEGMENT OPTIONS & CONCEPT TEMPLATE
   ═══════════════════════════════════════════════════════════════════════════ */

// Segment options by population
const VITAMIN_SEGMENT_OPTIONS = [
  {
    name: "Moms with Kids (4-12)",
    description: "Mothers whose children are exclusively school-aged (4-12).",
    profiles: 59
  },
  {
    name: "Moms with Teens (13-19)",
    description: "Mothers whose children are exclusively adolescents aged 13-19.",
    profiles: 21
  },
  {
    name: "Moms with Kids and Teens",
    description: "Mothers with children in BOTH age groups (4-12 and 13-19).",
    profiles: 21
  },
  {
    name: "Teens (13-19)",
    description: "Teenagers aged 13-19 who take vitamins themselves.",
    profiles: 25
  }
];

const DOG_FOOD_SEGMENT_OPTIONS = [
  {
    name: "People Who Already Decided (And Won't Give It Any Time)",
    description: "Reliable dog owners focused on consistent, quality nutrition.",
    profiles: 85
  },
  {
    name: "People Guarding Hard-Won Stability",
    description: "Established pet parents with stable routines and preferences.",
    profiles: 74
  },
  {
    name: "Loving People Under Pressure",
    description: "Dedicated owners actively seeking the best for their dogs.",
    profiles: 42
  },
  {
    name: "People Proving They Care With Focused Effort",
    description: "Careful selectors who scrutinize ingredients and sourcing.",
    profiles: 35
  },
  {
    name: "People Who Have Researched Everything",
    description: "Data-oriented owners who optimize based on research and reviews.",
    profiles: 17
  }
];

const CAT_FOOD_SEGMENT_OPTIONS = [
  {
    name: "Loving People Under Pressure",
    description: "They know what good looks like and they know they're not delivering it. The gap is constraint, not ignorance — budget squeeze plus the fear that a premium experiment ends in a refused bowl.",
    profiles: 64
  },
  {
    name: "People Who Are Happy Enough",
    description: "Settled competence. They figured out what works, filed it under 'solved,' and moved on. The cat is loved but kept in perspective: a beloved pet, not an identity.",
    profiles: 58
  },
  {
    name: "People Who Trust and Move On",
    description: "Competent delegators who treat cat ownership the way they treat their careers: find the right authority, follow the protocol, don't second-guess. The vet sets the frame.",
    profiles: 57
  },
  {
    name: "People Who Love Through Quality",
    description: "Feeding is a moral obligation and the daily proof of competent love. They distrust the industry, claim protective authority over the bowl, and stay in permanent research mode.",
    profiles: 40
  },
  {
    name: "Quietly Competent People",
    description: "Did the homework once, found something defensible, and moved on. Affluent and educated enough to know better options exist — and they've deliberately chosen not to.",
    profiles: 31
  }
];

// KNF (Kettle & Fire) segment options - Bone Broth Consumers
const KNF_SEGMENT_OPTIONS = [
  {
    name: "Clean Gatekeepers",
    description: "They flip every package, scan every ingredient, and refuse anything they can't pronounce — that vigilance is the job they've claimed as the household's consumer.",
    profiles: 28
  },
  {
    name: "Ritual Sippers",
    description: "Bone broth is their pause button. Stretched thin by caregiving, they sip it warm to feel briefly nourished and looked after.",
    profiles: 11
  },
  {
    name: "Body Optimizers",
    description: "Bone broth is fuel — protein grams, calorie ratio, macros that fit a daily target. Disciplined, goal-oriented, and pragmatic.",
    profiles: 17
  },
  {
    name: "Flavor Architects",
    description: "Bone broth has one job — make the dish taste like they spent all day on it. Competence-driven home cooks who want depth, richness, restaurant-quality results.",
    profiles: 20
  },
  {
    name: "Budget Pragmatists",
    description: "Bone broth is a cooking ingredient, not a wellness product. They want consistent, predictable meals at a price the budget can absorb.",
    profiles: 16
  }
];

// Archer Jerky segment options - Meat Snack Consumers
const ARCHER_JERKY_SEGMENT_OPTIONS = [
  {
    name: "Ingredient Purists",
    description: "Health disciplinarians who've converted to clean-label brands. Control is their core — over ingredients, energy, and the example they set. Chomps owns their trust; the challenge is share consolidation.",
    profiles: 35
  },
  {
    name: "Protein Optimizers",
    description: "Fitness pragmatists using meat snacks to hit protein targets and manage GLP-1 effects. The snack serves a larger project — fitness, longevity — not identity. They need it, they don't love it.",
    profiles: 29
  },
  {
    name: "Budget-Bound Aspirers",
    description: "The largest segment, caught between mainstream brands and clean-label aspirations. Overwhelmingly mothers with a conscious gap between values and behavior — working to close it but not yet arrived.",
    profiles: 41
  },
  {
    name: "Grab-and-Goers",
    description: "Pragmatic backbone of the category. High-income but budget-minded on snacks. Zero emotional investment, inertial loyalty to Jack Link's and Slim Jim. Win them with distribution and value pricing.",
    profiles: 33
  },
  {
    name: "Lunchbox Moms",
    description: "Almost exclusively mothers buying for kids, not themselves. Price-sensitive, focused on safety and lunchbox fit. The child has veto power — one rejection kills a brand for this household.",
    profiles: 22
  },
  {
    name: "Comfort Snackers",
    description: "Smallest but most emotionally complex segment. They snack for comfort and nostalgia — the beer-and-Slim-Jim ritual. Pleasure wins over clean eating; switching requires a life event, not marketing.",
    profiles: 8
  }
];

// Map populations to their segment options
const POPULATION_SEGMENTS: Record<string, typeof VITAMIN_SEGMENT_OPTIONS> = {
  'population_vitamin_survey_enriched_copy': VITAMIN_SEGMENT_OPTIONS,
  'population_wp_dog_food': DOG_FOOD_SEGMENT_OPTIONS,
  'population_wp_cat_food': CAT_FOOD_SEGMENT_OPTIONS,
  'population_knf_bone_broth': KNF_SEGMENT_OPTIONS,
  'population_archer_jerky': ARCHER_JERKY_SEGMENT_OPTIONS,
};

// Populations that support segment selection
const POPULATIONS_WITH_SEGMENTS = Object.keys(POPULATION_SEGMENTS);

// Demo user populations
const DEMO_USER_POPULATIONS: PopulationOption[] = [
  { name: 'population_wp_dog_food', agent_count: 253, displayName: 'Dog Food Buyers' },
  { name: 'population_wp_cat_food', agent_count: 225, displayName: 'Cat Food Buyers' },
  { name: 'population_archer_jerky', agent_count: 168, displayName: 'Jerky Consumers' }
];

// WellnessPet user populations
const WELLNESSPET_USER_POPULATIONS: PopulationOption[] = [
  { name: 'population_wp_dog_food', agent_count: 253, displayName: 'Dogs' },
  { name: 'population_wp_cat_food', agent_count: 250, displayName: 'Cats' }
];

// Mondelez user populations
const MONDELEZ_USER_POPULATIONS: PopulationOption[] = [
  { name: 'population_mondelez_cookies', agent_count: 71, displayName: 'Cookies' }
];

// KNF (Kettle & Fire) user populations
const KNF_USER_POPULATIONS: PopulationOption[] = [
  { name: 'population_knf_bone_broth', agent_count: 92, displayName: 'Bone Broth Consumers' }
];

// Archerjerky user populations
const ARCHERJERKY_USER_POPULATIONS: PopulationOption[] = [
  { name: 'population_archer_jerky', agent_count: 168, displayName: 'Jerky Consumers' }
];

// Internal form steps
type FormStep = 'concept-details' | 'segment-selection';

const CONCEPT_TEMPLATE = `**Product Headline:** Your Product Name

**Brand Name:** Your Brand Name

**One Line Description:** A brief statement about the problem your product solves or the need it addresses.

**Detailed Description:** Describe your product in detail. What makes it unique? Who is it for? What benefits does it provide? Include key features, ingredients, or specifications.

**Reasons to Believe:**
- First key claim or benefit
- Second key claim or benefit
- Third key claim or benefit
- Fourth key claim or benefit (optional)

**Price & Pack:** $XX.XX for XX units`;

/* ═══════════════════════════════════════════════════════════════════════════
   TYPES
   ═══════════════════════════════════════════════════════════════════════════ */

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

interface VitaminConceptTestFormProps {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void;
  initialFormData?: Record<string, any>;
  onFormDataChange?: (data: Record<string, any>) => void;
  onSavePayload?: (payload: Record<string, any>) => void | Promise<void>;
}

interface Concept {
  id: string;
  conceptStr: string;
  fileName: string | null;
  fileSize: number | null;
  brandName: string;
  imageDataUrl: string | null;
  parsing?: boolean;
}

// Flow: name -> concept -> population (optional) -> segments (optional) -> review
type Step = 'name' | 'concept' | 'population' | 'segments' | 'review';

/* ═══════════════════════════════════════════════════════════════════════════
   HELPER COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

const QNumber: React.FC<{ index: number; total: number }> = ({ index, total }) => (
  <div className="vct-q-num">
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Icon.arrowRight style={{ width: 11, height: 11 }} />
      {index} <span style={{ color: 'var(--dim)', fontWeight: 400 }}>/ {total}</span>
    </span>
  </div>
);

const OkRow: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  label?: string;
  showHint?: boolean;
}> = ({ onClick, disabled = false, label = "OK", showHint = true }) => (
  <div className="vct-ok-row">
    <button className="vct-ok-btn" onClick={onClick} disabled={disabled}>
      {label}
      <Icon.check style={{ width: 14, height: 14 }} />
    </button>
    {showHint && (
      <span className="vct-enter-hint">
        press <span className="vct-kbd">Enter ↵</span>
      </span>
    )}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   HELPER: Convert **text** to <strong>text</strong> for contentEditable
   ═══════════════════════════════════════════════════════════════════════════ */

const toHtml = (text: string): string => {
  // Convert **text** to <strong>text</strong>
  // Then convert newlines to <br>
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br>');
};

const fromHtml = (html: string): string => {
  // Convert <strong>text</strong> back to **text**
  // And <br> back to newlines
  return html
    .replace(/<strong>(.+?)<\/strong>/gi, '**$1**')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, ''); // Remove any other HTML tags
};

/* ═══════════════════════════════════════════════════════════════════════════
   SAMPLE MODAL
   ═══════════════════════════════════════════════════════════════════════════ */

const SampleModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  if (!open) return null;

  return (
    <div className="vct-modal-overlay" onClick={onClose}>
      <div className="vct-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="vct-modal__head">
          <h3 className="vct-modal__title">Sample concept card</h3>
          <button className="vct-modal__close" onClick={onClose}>
            <Icon.x style={{ width: 18, height: 18 }} />
          </button>
        </div>
        <div className="vct-modal__body">
          <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 16, lineHeight: 1.5 }}>
            Your concept card should look something like this. Upload an image or write manually.
          </p>

          {/* Visual concept card mockup */}
          <div className="vct-sample-visual">
            <div className="vct-sample-visual__card">
              {/* Left side - product image */}
              <div className="vct-sample-visual__left">
                <div className="vct-sample-visual__img">
                  <Icon.image style={{ width: 48, height: 48, opacity: 0.4 }} />
                  <span>Product Image</span>
                </div>
                <div className="vct-sample-visual__price-box">
                  <div className="vct-sample-visual__price">$24.99</div>
                  <div className="vct-sample-visual__pack">for 30 units</div>
                </div>
              </div>

              {/* Right side - content */}
              <div className="vct-sample-visual__right">
                <div className="vct-sample-visual__brand">BRAND NAME</div>
                <h4 className="vct-sample-visual__headline">Product Headline Goes Here</h4>
                <p className="vct-sample-visual__oneline">
                  A brief statement about the problem your product solves.
                </p>
                <p className="vct-sample-visual__desc">
                  Detailed description of your product. What makes it unique? Who is it for? Include key features and benefits that set it apart.
                </p>
                <div className="vct-sample-visual__rtb">
                  <div className="vct-sample-visual__rtb-title">Key Benefits:</div>
                  <ul>
                    <li>First key claim or benefit</li>
                    <li>Second key claim or benefit</li>
                    <li>Third key claim or benefit</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Annotations */}
            <div className="vct-sample-visual__annotations">
              <div className="vct-sample-visual__anno"><span className="vct-sample-visual__dot" />Product image</div>
              <div className="vct-sample-visual__anno"><span className="vct-sample-visual__dot" />Brand name</div>
              <div className="vct-sample-visual__anno"><span className="vct-sample-visual__dot" />Product headline</div>
              <div className="vct-sample-visual__anno"><span className="vct-sample-visual__dot" />Description</div>
              <div className="vct-sample-visual__anno"><span className="vct-sample-visual__dot" />Claims / RTBs</div>
              <div className="vct-sample-visual__anno"><span className="vct-sample-visual__dot" />Price & pack</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const VitaminConceptTestForm: React.FC<VitaminConceptTestFormProps> = ({
  onBack,
  onSavePayload,
  onFormDataChange,
  initialFormData = {},
}) => {
  // Inject styles
  useEffect(() => { injectStyles(); }, []);

  // Auth state
  const { isAdmin, isSmartypantsUser, isBacchusUser, isDemoUser, isWellnesspetUser, isMondelezUser, isKnfUser, isArcherjerkyUser } = useAuth();

  // State
  const [step, setStep] = useState<Step>('name');
  const [conceptIndex, setConceptIndex] = useState(0);
  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [count, setCount] = useState<string>(initialFormData.count || '1');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [concepts, setConcepts] = useState<Concept[]>(() => {
    if (initialFormData.concepts?.length) return initialFormData.concepts;
    return [{ id: Math.random().toString(36).slice(2), conceptStr: '', fileName: null, fileSize: null, brandName: '', imageDataUrl: null }];
  });
  const [segments, setSegments] = useState<string[]>(
    initialFormData.segments || ['Moms with Kids (4–12)']
  );

  // Admin options
  const [showAdmin, setShowAdmin] = useState(false);
  const [populations, setPopulations] = useState<PopulationOption[]>([]);
  const [populationsLoading, setPopulationsLoading] = useState(true);
  const [selectedPopulation, setSelectedPopulation] = useState(
    isBacchusUser ? 'population_mondelez_cookies' : 'population_vitamin_survey_enriched_copy'
  );
  const [sampleSize, setSampleSize] = useState(isBacchusUser ? 71 : 126);
  const [enableNorms, setEnableNorms] = useState(false);
  const [enableAgentSearch, setEnableAgentSearch] = useState(false);
  const [enableNovelReasoning, setEnableNovelReasoning] = useState(false);
  const [researchQuestion, setResearchQuestion] = useState('');
  const [selectedSegments, setSelectedSegments] = useState<string[]>(initialFormData.segments || []);

  // Determine if segment step should be shown (for users with populations that have segments)
  const needsSegmentStep = POPULATIONS_WITH_SEGMENTS.includes(selectedPopulation);

  // Get the segment options for the current population
  const currentSegmentOptions = POPULATION_SEGMENTS[selectedPopulation] || [];

  // Internal step state
  const [currentStep, setCurrentStep] = useState<FormStep>('concept-details');

  // UI state
  const [showSample, setShowSample] = useState(false);
  const [drag, setDrag] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [showIntroBanner, setShowIntroBanner] = useState(false);
  useEffect(() => {
    if (localStorage.getItem('vitamin_concept_test_intro_seen') !== 'true') {
      setShowIntroBanner(true);
    }
  }, []);
  const dismissIntroBanner = () => {
    localStorage.setItem('vitamin_concept_test_intro_seen', 'true');
    setShowIntroBanner(false);
  };
  const fileRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const lastExternalValue = useRef<string>('');
  const prevShowManualEntry = useRef<boolean>(false);
  const prevConceptIndex = useRef<number>(0);

  // Convert markdown bold to HTML
  const toHtml = useCallback((text: string) => {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  }, []);

  // Update editor content only when modal opens or concept index changes
  useEffect(() => {
    const currentConcept = concepts[conceptIndex];
    const val = currentConcept?.conceptStr || '';

    // Only update innerHTML when:
    // 1. Modal just opened (was closed, now open)
    // 2. Concept index changed while modal is open
    const modalJustOpened = showManualEntry && !prevShowManualEntry.current;
    const conceptChanged = showManualEntry && conceptIndex !== prevConceptIndex.current;

    if (editorRef.current && (modalJustOpened || conceptChanged)) {
      // If opening a new empty concept, show the template for guidance
      // but don't save it to state yet (so checkmark doesn't appear until user edits)
      const displayValue = (modalJustOpened && !val.trim()) ? CONCEPT_TEMPLATE : val;
      editorRef.current.innerHTML = toHtml(displayValue);
      lastExternalValue.current = displayValue;

      // Focus and move cursor to end
      editorRef.current.focus();
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }

    // Update refs for next render
    prevShowManualEntry.current = showManualEntry;
    prevConceptIndex.current = conceptIndex;
  }, [concepts, conceptIndex, toHtml, showManualEntry]);

  // Initialize populations based on user type (non-admin users)
  useEffect(() => {
    if (!isAdmin) {
      // Demo users get dropdown with 2 population options
      if (isDemoUser) {
        setPopulations(DEMO_USER_POPULATIONS);
        setSelectedPopulation(DEMO_USER_POPULATIONS[0].name);
        setSampleSize(DEMO_USER_POPULATIONS[0].agent_count);
        setPopulationsLoading(false);
        return;
      }
      // WellnessPet users get dropdown with dog/cat population options
      if (isWellnesspetUser) {
        setPopulations(WELLNESSPET_USER_POPULATIONS);
        setSelectedPopulation(WELLNESSPET_USER_POPULATIONS[0].name);
        setSampleSize(WELLNESSPET_USER_POPULATIONS[0].agent_count);
        setPopulationsLoading(false);
        return;
      }
      // Mondelez users get fixed cookie population (no dropdown)
      if (isMondelezUser) {
        setPopulations(MONDELEZ_USER_POPULATIONS);
        setSelectedPopulation(MONDELEZ_USER_POPULATIONS[0].name);
        setSampleSize(MONDELEZ_USER_POPULATIONS[0].agent_count);
        setPopulationsLoading(false);
        return;
      }
      // Bacchus users get fixed population (no dropdown)
      if (isBacchusUser) {
        setSelectedPopulation('population_mondelez_cookies');
        setSampleSize(71);
        setPopulationsLoading(false);
        return;
      }
      // KNF (Kettle & Fire) users get bone broth population with segment selection
      if (isKnfUser) {
        setPopulations(KNF_USER_POPULATIONS);
        setSelectedPopulation(KNF_USER_POPULATIONS[0].name);
        setSampleSize(KNF_USER_POPULATIONS[0].agent_count);
        setPopulationsLoading(false);
        return;
      }
      // Archerjerky users get jerky population
      if (isArcherjerkyUser) {
        setPopulations(ARCHERJERKY_USER_POPULATIONS);
        setSelectedPopulation(ARCHERJERKY_USER_POPULATIONS[0].name);
        setSampleSize(ARCHERJERKY_USER_POPULATIONS[0].agent_count);
        setPopulationsLoading(false);
        return;
      }
      // Other non-admin users get vitamin population
      setSelectedPopulation('population_vitamin_survey_enriched_copy');
      setPopulationsLoading(false);
      return;
    }
  }, [isAdmin, isDemoUser, isWellnesspetUser, isMondelezUser, isBacchusUser, isKnfUser, isArcherjerkyUser]);

  // Fetch populations for admin only
  useEffect(() => {
    if (!isAdmin) return; // Only fetch for admins, non-admins use pre-defined populations

    const fetchPopulations = async () => {
      try {
        const resp = await fetch(`${API_URL}/vitamin/populations`, { credentials: 'include' });
        if (resp.ok) {
          const data = await resp.json();
          const pops = data.populations || data || [];
          setPopulations(pops);
          // Set default population if not already set
          if (pops.length > 0 && !selectedPopulation) {
            setSelectedPopulation(pops[0].name);
          }
        }
      } catch (e) {
        console.error('Failed to fetch populations:', e);
      } finally {
        setPopulationsLoading(false);
      }
    };
    fetchPopulations();
  }, [isAdmin]);

  // Count is now derived from concepts (for backwards compatibility with form data)
  useEffect(() => {
    const validCount = concepts.filter(c => c.conceptStr.trim()).length;
    if (validCount >= 5) setCount('5+');
    else if (validCount > 0) setCount(String(validCount));
    else setCount('1');
  }, [concepts]);

  // Clear segments when population changes (different populations have different segments)
  useEffect(() => {
    setSegments([]);
  }, [selectedPopulation]);

  // Update sample size when demo or WellnessPet user changes population
  useEffect(() => {
    if ((isDemoUser || isWellnesspetUser) && selectedPopulation) {
      const userPopulations = isDemoUser ? DEMO_USER_POPULATIONS : WELLNESSPET_USER_POPULATIONS;
      const selectedPop = userPopulations.find(p => p.name === selectedPopulation);
      if (selectedPop) {
        setSampleSize(selectedPop.agent_count);
      }
    }
  }, [isDemoUser, isWellnesspetUser, selectedPopulation]);

  // Update concept
  const updateConcept = useCallback((idx: number, patch: Partial<Concept>) => {
    setConcepts(prev => {
      const next = [...prev];
      const merged = { ...next[idx], ...patch };
      const m = (merged.conceptStr || '').match(/Brand Name:\**\s*(.+?)(?:\n|$)/);
      merged.brandName = m ? m[1].replace(/\*+/g, '').trim() : '';
      next[idx] = merged;
      return next;
    });
  }, []);

  // Toggle segment
  const toggleSegment = (name: string) => {
    setSegments(prev => prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]);
  };

  // Select all segments
  const selectAllSegments = () => {
    setSegments(currentSegmentOptions.map(s => s.name));
  };

  // Clear all segments
  const clearAllSegments = () => {
    setSegments([]);
  };

  // Notify form data change
  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange({ simName, count, concepts, segments, selectedPopulation, sampleSize });
    }
  }, [simName, count, concepts, segments, selectedPopulation, sampleSize]);

  // Check if we need optional steps
  const hasPopulationStep = populations.length > 1;
  const hasSegmentsStep = currentSegmentOptions.length > 0;

  // Progress calculation - name + (population?) + concept + (segments?) + review
  // Base: name + concept + review = 3 steps
  // Add 1 if population step, add 1 if segments step
  const totalQs = 3 + (hasPopulationStep ? 1 : 0) + (hasSegmentsStep ? 1 : 0);

  // Flow: name -> concept -> population (if multiple) -> segments (if defined) -> review
  const qIndexFor = (s: Step) => {
    let idx = 1;
    if (s === 'name') return idx;

    idx++;
    if (s === 'concept') return idx;

    if (hasPopulationStep) {
      idx++;
      if (s === 'population') return idx;
    }

    if (hasSegmentsStep) {
      idx++;
      if (s === 'segments') return idx;
    }

    idx++;
    if (s === 'review') return idx;

    return 0;
  };

  const progressPct = useMemo(() => {
    if (step === 'review') return 100;
    return (qIndexFor(step) / totalQs) * 100;
  }, [step, totalQs, hasPopulationStep, hasSegmentsStep]);

  // Navigation - Flow: name -> concept -> population (if multiple) -> segments (if defined) -> review
  const goNext = () => {
    if (step === 'name') { setStep('concept'); return; }
    if (step === 'concept') {
      // After concept, show population step if multiple populations
      if (hasPopulationStep) {
        setStep('population');
      } else if (hasSegmentsStep) {
        setStep('segments');
      } else {
        setStep('review');
      }
      return;
    }
    if (step === 'population') {
      // After population, show segments if available
      if (hasSegmentsStep) {
        setStep('segments');
      } else {
        setStep('review');
      }
      return;
    }
    if (step === 'segments') { setStep('review'); return; }
    if (step === 'review') { handleSubmit(); return; }
  };

  const goPrev = () => {
    if (step === 'name') { onBack(); return; }
    if (step === 'concept') { setStep('name'); return; }
    if (step === 'population') { setStep('concept'); return; }
    if (step === 'segments') {
      // Go back to population if available, else concept
      if (hasPopulationStep) {
        setStep('population');
      } else {
        setStep('concept');
      }
      return;
    }
    if (step === 'review') {
      // Go back to segments if available, else population if available, else concept
      if (hasSegmentsStep) {
        setStep('segments');
      } else if (hasPopulationStep) {
        setStep('population');
      } else {
        setStep('concept');
      }
      return;
    }
  };

  const canGoNext = useMemo(() => {
    if (step === 'name') return !!simName.trim();
    if (step === 'population') return !!selectedPopulation;
    // At least one concept must have content
    if (step === 'concept') return concepts.some(c => c.conceptStr.trim());
    if (step === 'segments') return segments.length > 0;
    if (step === 'review') return !!simName.trim();
    return true;
  }, [step, simName, selectedPopulation, concepts, segments]);

  const canGoPrev = step !== 'name';

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = ((e.target as Element)?.tagName || '').toLowerCase();
      const isEditing = tag === 'input' || tag === 'textarea';

      if (e.key === 'Enter' && !e.shiftKey) {
        if (isEditing && tag === 'textarea') return;
        if (canGoNext) { e.preventDefault(); goNext(); }
      }
      if (e.shiftKey && e.key === 'Tab' && canGoPrev) {
        e.preventDefault();
        goPrev();
      }
      // Population keyboard shortcuts (A-Z based on count)
      if (step === 'population' && !isEditing && /^[a-z]$/i.test(e.key)) {
        e.preventDefault();
        const idx = e.key.toLowerCase().charCodeAt(0) - 97;
        if (idx < populations.length) {
          setSelectedPopulation(populations[idx].name);
          setSampleSize(populations[idx].agent_count);
        }
      }
      // Segment keyboard shortcuts (A-D)
      if (step === 'segments' && !isEditing && /^[a-d]$/i.test(e.key)) {
        e.preventDefault();
        const idx = e.key.toLowerCase().charCodeAt(0) - 97;
        if (idx < currentSegmentOptions.length) toggleSegment(currentSegmentOptions[idx].name);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, canGoNext, canGoPrev, populations]);

  // Add a new empty concept
  const addConcept = () => {
    setConcepts(prev => [...prev, {
      id: Math.random().toString(36).slice(2),
      conceptStr: '',
      fileName: null,
      fileSize: null,
      brandName: '',
      imageDataUrl: null
    }]);
  };

  // Open manual editor - creates an empty concept and opens the modal
  // The checkmark will only appear after the user adds content and saves
  const openManualEditor = () => {
    const newId = Math.random().toString(36).slice(2);
    const newConcept = {
      id: newId,
      conceptStr: '',  // Start empty - user can use "Reset to template" if needed
      fileName: null,
      fileSize: null,
      brandName: '',
      imageDataUrl: null
    };
    setConcepts(prev => {
      // If there's only one empty concept, replace it; otherwise add new one
      if (prev.length === 1 && !prev[0].conceptStr.trim() && !prev[0].fileName) {
        return [newConcept];
      }
      return [...prev, newConcept];
    });
    setConceptIndex(concepts.length === 1 && !concepts[0].conceptStr.trim() && !concepts[0].fileName ? 0 : concepts.length);
    setShowManualEntry(true);
  };

  // Remove a concept by index
  const removeConcept = (index: number) => {
    setConcepts(prev => prev.filter((_, i) => i !== index));
  };

  // Handle multiple files upload
  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    // Process each file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      // Find an empty concept slot or create a new one
      const emptyIndex = concepts.findIndex(c => !c.conceptStr.trim() && !c.fileName);
      if (emptyIndex >= 0) {
        await handleFile(file, emptyIndex);
      } else {
        // Create a new concept and process the file into it
        const newId = Math.random().toString(36).slice(2);
        const newIndex = concepts.length;
        setConcepts(prev => [...prev, {
          id: newId,
          conceptStr: '',
          fileName: null,
          fileSize: null,
          brandName: '',
          imageDataUrl: null
        }]);
        // Small delay to ensure state updates
        await new Promise(r => setTimeout(r, 50));
        await handleFile(file, newIndex);
      }
    }
  };

  // File handling - now accepts optional targetIndex
  const handleFile = async (file: File | undefined, targetIndex?: number) => {
    if (!file) return;
    const idx = targetIndex ?? conceptIndex;

    // Validate file size (max 50MB)
    const MAX_SIZE_MB = 50;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      console.error(`Image too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Maximum size is ${MAX_SIZE_MB}MB.`);
      return;
    }

    // Validate file type
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
    if (!ALLOWED_TYPES.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|webp|gif|heic|heif)$/i)) {
      console.error('Unsupported file type. Please use JPEG, PNG, WebP, GIF, or HEIC.');
      return;
    }

    // Immediately read image and show placeholder with loading state
    const readImageAsDataUrl = (): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    };

    try {
      // First, read the image and show it immediately with parsing state
      const imageDataUrl = await readImageAsDataUrl();
      updateConcept(idx, {
        fileName: file.name,
        fileSize: file.size,
        imageDataUrl,
        parsing: true,
      });
    } catch {
      // If image read fails, still show placeholder
      updateConcept(idx, {
        fileName: file.name,
        fileSize: file.size,
        parsing: true,
      });
    }

    try {
      // Field schema required by the API
      const fieldSchema = {
        fields: [
          { name: 'productHeadline', type: 'str', description: 'Product name/headline (main title)', required: true },
          { name: 'brandName', type: 'str', description: 'Brand/manufacturer name', required: true },
          { name: 'consumerInsight', type: 'str', description: 'One line Description - problem this solves', required: false },
          { name: 'mainBenefit', type: 'str', description: 'Detailed Description - main value proposition', required: true },
          { name: 'reasonsToBelieve', type: 'list[str]', description: 'Reasons to believe - key claims', required: false },
          { name: 'pricePoint', type: 'str', description: 'Price & Pack info', required: false },
          { name: 'packSize', type: 'str', description: 'Pack size/quantity', required: false },
        ],
        context: 'consumer product concept test'
      };

      const formData = new FormData();
      formData.append('image', file);
      formData.append('field_schema', JSON.stringify(fieldSchema));

      const response = await fetch(`${API_URL}/parse-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to parse image');
      }

      const data = await response.json();

      // Format parsed data to text (matching original format)
      const parts: string[] = [];
      if (data.productHeadline) parts.push(`**Product Headline:** ${data.productHeadline}`);
      if (data.brandName) parts.push(`**Brand Name:** ${data.brandName}`);
      if (data.consumerInsight) parts.push(`**One Line Description:** ${data.consumerInsight}`);
      if (data.mainBenefit) parts.push(`**Detailed Description:** ${data.mainBenefit}`);

      const rtbs = data.reasonsToBelieve?.filter(Boolean) || [];
      if (rtbs.length > 0) {
        parts.push(`**Reasons to Believe:**\n${rtbs.map((r: string) => `- ${r}`).join('\n')}`);
      }

      const pricePack = [data.pricePoint, data.packSize].filter(Boolean).join(' for ');
      if (pricePack) parts.push(`**Price & Pack:** ${pricePack}`);

      const conceptStr = parts.join('\n\n');

      // Update with parsed data (imageDataUrl already set from initial read)
      updateConcept(idx, {
        conceptStr,
        brandName: data.brandName || '',
        parsing: false,
      });
    } catch (err) {
      console.error('Image parsing failed:', err);
      // Fallback auto-fill (imageDataUrl already set from initial read)
      const auto = `**Product Headline:** ${file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ')}
**Brand Name:** Imported from upload
**One Line Description:** Auto-extracted from your concept card. Edit anything below.
**Detailed Description:** We pulled this from "${file.name}". Replace this paragraph with your full benefit story.

**Reasons to Believe:**
- Add your first claim
- Add your second claim
- Add your third claim

**Price & Pack:** $0.00 for 0 units`;

      updateConcept(idx, { conceptStr: auto, parsing: false });
    }
  };

  // Submit
  const handleSubmit = async () => {
    // Build payload - must match the API format expected by /simulations endpoint
    const basePayload = {
      task: 'vitamin-concept-test',
      name: simName || 'Concept Test',
      population: selectedPopulation,
      sample_size: sampleSize ?? 126,
      enable_norms: enableNorms,
    };

    let payload: Record<string, any>;

    // Filter out empty concepts
    const validConcepts = concepts.filter(c => c.conceptStr.trim());
    const mode = validConcepts.length === 1 ? 'single' : 'multi';

    if (mode === 'single') {
      // Single concept mode - use concept_str directly
      payload = {
        ...basePayload,
        concept_str: validConcepts[0].conceptStr,
      };
    } else {
      // Multi-concept mode - use concepts array
      payload = {
        ...basePayload,
        concepts: validConcepts.map(c => ({
          concept_str: c.conceptStr,
        })),
      };
    }

    // Add segments if selected
    if (segments.length > 0) {
      payload.segments = segments;
    }

    // Add admin flags if enabled
    if (enableAgentSearch) {
      payload.enable_agent_search = true;
    }
    if (enableNovelReasoning) {
      payload.enable_novel_reasoning = true;
    }
    if (researchQuestion.trim()) {
      payload.research_question = researchQuestion.trim();
    }

    // Enable norms for single concept tests with dog/cat food populations
    if (mode === 'single' && (selectedPopulation === 'population_wp_dog_food' || selectedPopulation === 'population_wp_cat_food')) {
      payload.enable_norms = true;
    }

    try {
      if (onSavePayload) {
        await onSavePayload(payload);
      }
    } catch (e) {
      console.error('Submit error:', e);
    }
  };

  // Calculate totals
  const totalProfiles = segments.reduce((sum, name) => {
    const s = currentSegmentOptions.find(x => x.name === name);
    return sum + (s ? s.profiles : 0);
  }, 0) || 126;

  const handleEdit = (target: string) => {
    if (target === 'name') setStep('name');
    else if (target === 'population') setStep('population');
    else if (target === 'concept') setStep('concept');
    else if (target === 'segments') setStep('segments');
  };

  const concept = concepts[conceptIndex];
  const qIdx = qIndexFor(step, conceptIndex);

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════════════════ */

  return (
    <div className={`vct-app ${showIntroBanner ? 'vct-app--banner' : ''}`}>
      {/* Progress bar */}
      <div className="vct-progress">
        <div className="vct-progress__fill" style={{ width: `${progressPct}%` }} />
      </div>

      {/* Experimental UI intro banner — shown once per user */}
      {showIntroBanner && (
        <div className="vct-banner-wrap">
          <div className="vct-banner vct-banner-enter">
            <div className="vct-banner__icon"><Icon.sparkles /></div>
            <div className="vct-banner__body">
              <strong>You're trying our new concept-test UI.</strong>
              <span> It looks and feels different on purpose — share feedback if anything's off.</span>
            </div>
            <button className="vct-banner__cta" onClick={dismissIntroBanner}>Got it</button>
            <button className="vct-banner__close" onClick={dismissIntroBanner} aria-label="Dismiss">
              <Icon.x />
            </button>
          </div>
        </div>
      )}

      {/* ═══ NAME SCREEN ═══ */}
      {step === 'name' && (
        <div className="vct-screen vct-screen-enter">
          <div className="vct-screen__inner">
            <QNumber index={qIdx} total={totalQs} />
            <h1 className="vct-q-title">What should we call this <em>simulation?</em></h1>
            <p className="vct-q-help">A short label so you can find it later — feel free to skip and we'll name it for you.</p>

            <input
              className="vct-big-input"
              value={simName}
              placeholder="e.g. CalmKids Gummies — Wave 1"
              onChange={e => setSimName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') goNext(); }}
              autoFocus
            />

            <OkRow onClick={goNext} disabled={!simName.trim()} />
          </div>
        </div>
      )}

      {/* ═══ POPULATION SCREEN ═══ */}
      {step === 'population' && (
        <div className="vct-screen vct-screen-enter">
          <div className="vct-screen__inner">
            <QNumber index={qIdx} total={totalQs} />
            <h1 className="vct-q-title">Select your <em>audience</em></h1>
            <p className="vct-q-help">Choose the consumer population for your concept test.</p>

            <div className="vct-choices">
              {populations.map((pop, i) => {
                const isSel = selectedPopulation === pop.name;
                return (
                  <button
                    key={pop.name}
                    className={`vct-choice ${isSel ? 'vct-choice--selected' : ''}`}
                    onClick={() => {
                      setSelectedPopulation(pop.name);
                      setSampleSize(pop.agent_count);
                    }}
                  >
                    <span className="vct-choice__key">{String.fromCharCode(65 + i)}</span>
                    <div style={{ flex: 1 }}>
                      <div className="vct-choice__label">{pop.displayName || pop.name}</div>
                    </div>
                    <span className="vct-choice__meta">{pop.agent_count} profiles</span>
                    <Icon.check className="vct-choice__check" />
                  </button>
                );
              })}
            </div>

            <OkRow onClick={goNext} disabled={!selectedPopulation} />
          </div>
        </div>
      )}

      {/* ═══ CONCEPT SCREEN ═══ */}
      {step === 'concept' && (() => {
        const hasAny = concepts.some(c => c.fileName || c.conceptStr.trim());
        const validCount = concepts.filter(c => c.conceptStr.trim()).length;

        const getCardLabel = (c: typeof concepts[0]) => {
          if (c.brandName) return c.brandName;
          if (c.fileName) return c.fileName;
          if (c.conceptStr) {
            const h = c.conceptStr.match(/Product Headline:\**\s*(.+?)(?:\n|$)/);
            if (h) return h[1].replace(/\*+/g, '').trim();
          }
          return 'Untitled';
        };

        return (
          <div className="vct-screen vct-screen-enter">
            <div className="vct-screen__inner">
              <QNumber index={qIdx} total={totalQs} />
              <h1 className="vct-q-title">Upload your <em>concepts.</em></h1>
              <p className="vct-q-help">
                {hasAny
                  ? `${validCount} concept${validCount === 1 ? '' : 's'} ready. Add more, edit, or continue.`
                  : "Drop in one or more concept cards — we'll extract everything automatically."}
              </p>

              {/* EMPTY STATE */}
              {!hasAny && (
                <>
                  {/* Big Dropzone */}
                  <div
                    className={`vct-dropzone ${drag ? 'vct-dropzone--drag' : ''}`}
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDrag(false);
                      handleFiles(e.dataTransfer.files);
                    }}
                    style={{ padding: '44px 28px' }}
                  >
                    <div className="vct-dropzone__icon">
                      {parsing
                        ? <div className="vct-spinner" style={{ width: 22, height: 22, borderWidth: 2 }} />
                        : <Icon.upload style={{ width: 20, height: 20 }} />}
                    </div>
                    <div className="vct-dropzone__title">
                      {parsing ? 'Reading your concept cards…' : <>Drop one or more concept cards or <a>browse</a></>}
                    </div>
                    <div className="vct-dropzone__hint">PNG · JPG · PDF · PPT — up to 25MB each</div>
                    <input
                      ref={fileRef}
                      type="file"
                      hidden
                      multiple
                      accept="image/*,.pdf,.ppt,.pptx"
                      onChange={e => { handleFiles(e.target.files); if (e.target) e.target.value = ''; }}
                    />
                  </div>

                  {/* Requirements line */}
                  <div className="vct-req-line">
                    <span>Each card should include</span>
                    <strong>product headline</strong><span className="vct-req-line__sep" />
                    <strong>brand</strong><span className="vct-req-line__sep" />
                    <strong>description</strong><span className="vct-req-line__sep" />
                    <strong>claims</strong><span className="vct-req-line__sep" />
                    <strong>price & pack</strong>
                    <button type="button" className="vct-see-sample" onClick={() => setShowSample(true)}>
                      See sample
                    </button>
                  </div>

                  {/* OR divider */}
                  <div className="vct-or-divider">or</div>

                  {/* Write manually button */}
                  <button className="vct-secondary-btn" onClick={openManualEditor}>
                    <span className="vct-secondary-btn__icon"><Icon.fileText /></span>
                    <span className="vct-secondary-btn__body">
                      <div className="vct-secondary-btn__title">Write a concept manually</div>
                      <div className="vct-secondary-btn__sub">Use our guided template — fill in brand, claims, price & pack.</div>
                    </span>
                    <span className="vct-secondary-btn__arrow"><Icon.arrowRight /></span>
                  </button>
                </>
              )}

              {/* FILLED STATE */}
              {hasAny && (
                <>
                  {/* Concept Grid */}
                  <div className="vct-concept-grid">
                    {concepts.map((c, i) => {
                      if (!c.fileName && !c.conceptStr.trim()) return null;
                      const isWritten = !c.imageDataUrl && c.conceptStr.trim();
                      return (
                        <div
                          key={c.id}
                          className={`vct-concept-card ${c.parsing ? 'vct-concept-card--parsing' : ''}`}
                          onClick={() => {
                            setConceptIndex(i);
                            setShowManualEntry(true);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className={`vct-concept-card__thumb ${isWritten ? 'vct-concept-card__thumb--text' : ''}`}>
                            {c.imageDataUrl ? (
                              <img src={c.imageDataUrl} alt={c.brandName || `Concept ${i + 1}`} />
                            ) : (
                              <Icon.fileText style={{ width: 32, height: 32, opacity: 0.7 }} />
                            )}
                            <span className="vct-concept-card__source">
                              {c.imageDataUrl ? <><Icon.image style={{ width: 11, height: 11 }} /> Image</> : <><Icon.fileText style={{ width: 11, height: 11 }} /> Written</>}
                            </span>
                            <button
                              className="vct-concept-card__remove"
                              onClick={(e) => { e.stopPropagation(); removeConcept(i); }}
                              title="Remove"
                            >
                              <Icon.x />
                            </button>
                            {/* Done checkmark — appears when parsing finishes */}
                            {!c.parsing && c.conceptStr.trim() && (
                              <div className="vct-concept-card__done" aria-label="Concept ready">
                                <Icon.check />
                              </div>
                            )}
                            {/* Hover hint overlay */}
                            <div className="vct-concept-card__hover-hint">
                              <Icon.edit />
                              <span>Click to edit</span>
                            </div>
                          </div>
                          <div className="vct-concept-card__meta">
                            <div className="vct-concept-card__num">Concept {i + 1}</div>
                            <div className="vct-concept-card__name">{getCardLabel(c)}</div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Add Tile */}
                    <button
                      className={`vct-add-tile ${drag ? 'vct-dropzone--drag' : ''}`}
                      onClick={() => fileRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                      onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDrag(false);
                        handleFiles(e.dataTransfer.files);
                      }}
                    >
                      <span className="vct-add-tile__plus">
                        <Icon.upload style={{ width: 18, height: 18 }} />
                      </span>
                      <span>Upload more</span>
                      <span style={{ fontSize: 11, color: 'var(--dim)' }}>or drop here</span>
                      <input
                        ref={fileRef}
                        type="file"
                        hidden
                        multiple
                        accept="image/*,.pdf,.ppt,.pptx"
                        onChange={e => { handleFiles(e.target.files); if (e.target) e.target.value = ''; }}
                      />
                    </button>
                  </div>

                  {/* Requirements line */}
                  <div className="vct-req-line" style={{ marginTop: 14 }}>
                    <span>Each card should include</span>
                    <strong>product headline</strong><span className="vct-req-line__sep" />
                    <strong>brand</strong><span className="vct-req-line__sep" />
                    <strong>description</strong><span className="vct-req-line__sep" />
                    <strong>claims</strong><span className="vct-req-line__sep" />
                    <strong>price & pack</strong>
                    <button type="button" className="vct-see-sample" onClick={() => setShowSample(true)}>
                      See sample
                    </button>
                  </div>

                  {/* OR divider */}
                  <div className="vct-or-divider">or</div>

                  {/* Write manually button */}
                  <button className="vct-secondary-btn" onClick={openManualEditor}>
                    <span className="vct-secondary-btn__icon"><Icon.fileText /></span>
                    <span className="vct-secondary-btn__body">
                      <div className="vct-secondary-btn__title">Write a concept manually</div>
                      <div className="vct-secondary-btn__sub">Use our guided template — fill in brand, claims, price & pack.</div>
                    </span>
                    <span className="vct-secondary-btn__arrow"><Icon.arrowRight /></span>
                  </button>
                </>
              )}

              {/* Admin panel */}
              {isAdmin && (
                <div className="vct-admin-panel" style={{ marginTop: 20 }}>
                  <button className="vct-admin-panel__toggle" onClick={() => setShowAdmin(!showAdmin)}>
                    <Icon.settings style={{ width: 14, height: 14 }} />
                    Admin Options
                    <span style={{ marginLeft: 'auto' }}>{showAdmin ? '−' : '+'}</span>
                  </button>
                  {showAdmin && (
                    <div className="vct-admin-panel__content">
                      <div className="vct-admin-field">
                        <label>Population</label>
                        {populationsLoading ? (
                          <div style={{ fontSize: 14, color: 'var(--muted)', padding: '8px 0' }}>Loading populations...</div>
                        ) : populations.length > 0 ? (
                          <select
                            value={selectedPopulation}
                            onChange={e => setSelectedPopulation(e.target.value)}
                          >
                            {populations.map(p => (
                              <option key={p.name} value={p.name}>
                                {p.name} ({p.agent_count} agents)
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={selectedPopulation}
                            onChange={e => setSelectedPopulation(e.target.value)}
                            placeholder="e.g., population_vitamins"
                          />
                        )}
                      </div>
                      <div className="vct-admin-field">
                        <label>Sample Size</label>
                        <input
                          type="number"
                          value={sampleSize}
                          onChange={e => setSampleSize(parseInt(e.target.value) || 126)}
                          min={1}
                          max={500}
                        />
                      </div>
                      <div className="vct-admin-field">
                        <label>Enable Norms</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={enableNorms}
                            onChange={e => setEnableNorms(e.target.checked)}
                            style={{ width: 16, height: 16 }}
                          />
                          <span style={{ fontSize: 14 }}>Calculate norms on the fly</span>
                        </label>
                      </div>
                      <div className="vct-admin-field">
                        <label>Enable Agent Search</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={enableAgentSearch}
                            onChange={e => setEnableAgentSearch(e.target.checked)}
                            style={{ width: 16, height: 16 }}
                          />
                          <span style={{ fontSize: 14 }}>Enable agent search capability</span>
                        </label>
                      </div>
                      <div className="vct-admin-field">
                        <label>Enable Novel Reasoning</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={enableNovelReasoning}
                            onChange={e => setEnableNovelReasoning(e.target.checked)}
                            style={{ width: 16, height: 16 }}
                          />
                          <span style={{ fontSize: 14 }}>Enable novel reasoning mode</span>
                        </label>
                      </div>
                      <div className="vct-admin-field">
                        <label>Research Question</label>
                        <input
                          type="text"
                          value={researchQuestion}
                          onChange={e => setResearchQuestion(e.target.value)}
                          placeholder="Optional research question..."
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <OkRow
                onClick={goNext}
                disabled={!concepts.some(c => c.conceptStr.trim())}
                label="Continue"
              />
            </div>
          </div>
        );
      })()}

      {/* ═══ SEGMENTS SCREEN ═══ */}
      {step === 'segments' && (
        <div className="vct-screen vct-screen-enter">
          <div className="vct-screen__inner">
            <QNumber index={qIdx} total={totalQs} />
            <h1 className="vct-q-title">Who should we <em>ask?</em></h1>
            <p className="vct-q-help">Pick the audience segments you want feedback from. Select as many as you like.</p>

            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <button className="vct-tool-btn" onClick={selectAllSegments}>
                Select all
              </button>
              <button className="vct-tool-btn" onClick={clearAllSegments}>
                Clear
              </button>
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 13, color: 'var(--muted)', alignSelf: 'center' }}>
                {segments.length} of {currentSegmentOptions.length} selected
              </span>
            </div>

            <div className="vct-choices">
              {currentSegmentOptions.map((seg, i) => {
                const isSel = segments.includes(seg.name);
                return (
                  <button
                    key={seg.name}
                    className={`vct-choice ${isSel ? 'vct-choice--selected' : ''}`}
                    onClick={() => toggleSegment(seg.name)}
                  >
                    <span className="vct-choice__key">{String.fromCharCode(65 + i)}</span>
                    <div style={{ flex: 1 }}>
                      <div className="vct-choice__label">{seg.name}</div>
                      <div className="vct-choice__desc">{seg.description}</div>
                    </div>
                    <span className="vct-choice__meta">{seg.profiles} profiles</span>
                    <Icon.check className="vct-choice__check" />
                  </button>
                );
              })}
            </div>

            <OkRow onClick={goNext} disabled={segments.length === 0} />
          </div>
        </div>
      )}

      {/* ═══ REVIEW SCREEN ═══ */}
      {step === 'review' && (
        <div className="vct-screen vct-screen-enter">
          <div className="vct-screen__inner">
            <QNumber index={qIdx} total={totalQs} />
            <h1 className="vct-q-title">Ready to <em>launch?</em></h1>
            <p className="vct-q-help">Review the details below and click "Launch test" to start.</p>

            <div className="vct-review-card">
              <div className="vct-review-row">
                <div className="vct-review-row__label">Simulation</div>
                <div className="vct-review-row__value">
                  <strong style={{ color: 'var(--ink)' }}>{simName || 'Untitled'}</strong>
                </div>
                <button className="vct-review-row__edit" onClick={() => handleEdit('name')}>
                  <Icon.edit style={{ width: 13, height: 13 }} /> Edit
                </button>
              </div>
              <div className="vct-review-row">
                <div className="vct-review-row__label">Concepts</div>
                <div className="vct-review-row__value">
                  {concepts.filter(c => c.conceptStr.trim()).map((c, i) => {
                    const stripMd = (s: string) => s.replace(/\*+/g, '').trim();
                    const m = c.conceptStr.match(/Brand Name:\**\s*(.+?)(?:\n|$)/);
                    const h = c.conceptStr.match(/Product Headline:\**\s*(.+?)(?:\n|$)/);
                    const label = (h && stripMd(h[1])) || (m && stripMd(m[1])) || `Concept ${i + 1}`;
                    return (
                      <span key={i} className="vct-concept-chip">
                        <span className="vct-concept-chip__num">{i + 1}</span>
                        {label.length > 36 ? label.slice(0, 36) + '…' : label}
                      </span>
                    );
                  })}
                </div>
                <button className="vct-review-row__edit" onClick={() => handleEdit('concept')}>
                  <Icon.edit style={{ width: 13, height: 13 }} /> Edit
                </button>
              </div>
              {/* Population display for users with multiple populations */}
              {populations.length > 1 && (
                <div className="vct-review-row">
                  <div className="vct-review-row__label">Population</div>
                  <div className="vct-review-row__value">
                    <strong style={{ color: 'var(--ink)' }}>
                      {populations.find(p => p.name === selectedPopulation)?.displayName || selectedPopulation}
                    </strong>
                  </div>
                  <button className="vct-review-row__edit" onClick={() => handleEdit('population')}>
                    <Icon.edit style={{ width: 13, height: 13 }} /> Edit
                  </button>
                </div>
              )}
              <div className="vct-review-row">
                <div className="vct-review-row__label">Audience</div>
                <div className="vct-review-row__value">
                  {segments.length === 0
                    ? <span style={{ color: 'var(--muted)', fontWeight: 400 }}>None selected</span>
                    : segments.map((s, i) => (
                        <span key={s} style={{ color: 'var(--ink-soft)' }}>
                          {s}{i < segments.length - 1 ? ' · ' : ''}
                        </span>
                      ))}
                </div>
                <button className="vct-review-row__edit" onClick={() => handleEdit('segments')}>
                  <Icon.edit style={{ width: 13, height: 13 }} /> Edit
                </button>
              </div>
              <div className="vct-review-row">
                <div className="vct-review-row__label">Sample size</div>
                <div className="vct-review-row__value">
                  <strong style={{ color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>
                    {totalProfiles}
                  </strong>
                  <span style={{ color: 'var(--muted)', fontWeight: 400, marginLeft: 6 }}>
                    simulated consumers
                  </span>
                </div>
              </div>
            </div>

            <div className="vct-ok-row">
              <button
                className="vct-ok-btn"
                onClick={goNext}
                disabled={!simName.trim()}
                style={{
                  padding: '15px 26px',
                  fontSize: 17,
                  opacity: !simName.trim() ? 0.5 : 1,
                  cursor: !simName.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                Launch test
                <Icon.sparkles style={{ width: 16, height: 16 }} />
              </button>
              {simName.trim() && <span className="vct-enter-hint">press <span className="vct-kbd">Enter ↵</span></span>}
            </div>
          </div>
        </div>
      )}

      {/* Step nav */}
      <div className="vct-step-nav">
        <span className="vct-step-counter"><strong>{qIdx}</strong> / {totalQs}</span>
        <div className="vct-step-nav__btns">
          <button className="vct-step-nav__btn" onClick={goPrev} disabled={!canGoPrev}>
            <Icon.chevronUp />
          </button>
          <button className="vct-step-nav__btn" onClick={goNext} disabled={!canGoNext}>
            <Icon.chevronDown />
          </button>
        </div>
      </div>

      {/* Sample Modal */}
      <SampleModal open={showSample} onClose={() => setShowSample(false)} />

      {/* Manual Editor Modal */}
      {showManualEntry && concepts[conceptIndex] && (
        <div className="vct-modal-overlay" onClick={() => setShowManualEntry(false)}>
          <div className="vct-modal vct-modal--editor" onClick={e => e.stopPropagation()}>
            <div className="vct-modal__head">
              <div>
                <div className="vct-manual-modal__eyebrow">CONCEPT {conceptIndex + 1}</div>
                <h2 className="vct-modal__title">
                  {concepts[conceptIndex].brandName || 'Write your concept'}
                </h2>
              </div>
              <button className="vct-modal__close" onClick={() => setShowManualEntry(false)}>
                <Icon.x style={{ width: 18, height: 18 }} />
              </button>
            </div>
            <div className="vct-modal__body" style={{ padding: 0 }}>
              <div style={{ display: 'flex', gap: 8, padding: '12px 22px', borderBottom: '1px solid var(--border)', background: 'white' }}>
                <button
                  className="vct-tool-btn"
                  onClick={() => {
                    updateConcept(conceptIndex, { conceptStr: CONCEPT_TEMPLATE, brandName: 'Your Brand Name' });
                    lastExternalValue.current = CONCEPT_TEMPLATE;
                    if (editorRef.current) {
                      editorRef.current.innerHTML = toHtml(CONCEPT_TEMPLATE);
                    }
                  }}
                >
                  <Icon.fileText /> Reset to template
                </button>
                <button
                  className="vct-tool-btn"
                  onClick={() => {
                    updateConcept(conceptIndex, { conceptStr: '', brandName: '' });
                    lastExternalValue.current = '';
                    if (editorRef.current) {
                      editorRef.current.innerHTML = '';
                    }
                  }}
                >
                  <Icon.x /> Clear
                </button>
              </div>
              <div
                ref={editorRef}
                className="vct-manual-modal__textarea vct-concept-edit__textarea"
                contentEditable
                suppressContentEditableWarning
                data-placeholder="**Product Headline:** Your product name&#10;&#10;**Brand Name:** Your brand&#10;&#10;**One Line Description:** What problem does it solve?&#10;&#10;**Detailed Description:** Tell the full story..."
                onInput={(e) => {
                  const el = e.target as HTMLDivElement;
                  const text = el.innerText;
                  // Update lastExternalValue to prevent useEffect from resetting innerHTML
                  lastExternalValue.current = text;
                  // Extract brand name from text
                  const brandMatch = text.match(/Brand Name:\**\s*(.+?)(?:\n|$)/);
                  const brandName = brandMatch ? brandMatch[1].replace(/\*+/g, '').trim() : '';
                  updateConcept(conceptIndex, { conceptStr: text, brandName });
                }}
                style={{ minHeight: 400, outline: 'none' }}
              />
            </div>
            <div className="vct-modal__foot">
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                {concepts[conceptIndex].conceptStr.length} characters
              </span>
              <div style={{ flex: 1 }} />
              <button
                className="vct-ok-btn"
                onClick={() => setShowManualEntry(false)}
                disabled={!concepts[conceptIndex].conceptStr.trim()}
              >
                Save concept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VitaminConceptTestForm;
